import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import Swal from 'sweetalert2';
import { FiscalDocumentsService } from 'src/app/services/fiscal/fiscal-documents.service';
import { FiscalDocument } from 'src/app/services/fiscal/models/fiscal-document.interface';
import { take } from 'rxjs/operators';
import { Subscription, from } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CountryRetention } from 'src/app/services/fiscal/models/country-retention.interface';
import { MatDialog } from '@angular/material/dialog';

// Lista de países soportados para autosuggest de Google Maps
const SUPPORTED_COUNTRIES: Array<{name: string, code: string, retentionPercentage?: number}> = [
  { name: 'España', code: 'ES', retentionPercentage: 19 },
  { name: 'Francia', code: 'FR', retentionPercentage: 10 },
  { name: 'Portugal', code: 'PT', retentionPercentage: 15 },
  { name: 'Italia', code: 'IT', retentionPercentage: 12 },
  { name: 'Reino Unido', code: 'GB', retentionPercentage: 0 },
  { name: 'Alemania', code: 'DE', retentionPercentage: 0 }
];

interface ResidenceChangeRequest {
  id?: string;
  userId: string; // Los datos reales usan 'userId'
  userName?: string;
  userEmail?: string;
  currentCountry: string;
  requestedCountry: string;
  currentCountryRetentionPercentage?: number;
  requestedCountryRetentionPercentage?: number;
  currentAddress?: {
    street: string;
    locality: string;
    province: string;
    postalCode: string;
    country: string;
  };
  requestedAddress?: {
    street: string;
    locality: string;
    province: string;
    postalCode: string;
    country: string;
  };
  documentUrl?: string;
  requestDate: any;
  status: 'pending' | 'approved' | 'rejected' | 'expiring_soon' | 'expired';
  rejectionReason?: string;
  rejectionDate?: any;
  approvalDate?: any;
}

@Component({
  selector: 'app-admin-fiscal-documentation',
  templateUrl: './admin-fiscal-documentation.component.html',
  styleUrls: ['./admin-fiscal-documentation.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class AdminFiscalDocumentationComponent implements OnInit, OnDestroy {
  documents: any[] = [];
  filteredDocuments: any[] = [];
  isLoading: boolean = true;
  statusFilter: string = 'all';
  activeTab: string = 'documents'; // Default active tab
  
  // Filtros para documentos fiscales
  userFilter: string = '';
  dateFilter: string = '';
  
  // Para la gestión de retenciones por país
  countryRetentions: CountryRetention[] = [];
  isLoadingRetentions: boolean = false;
  newRetention: CountryRetention = {
    countryCode: '',
    countryName: '',
    percentage: 0
  };

  // Método para actualizar automáticamente el porcentaje cuando el país es "OTRO" o no está en el listado
  onCountryNameChange(): void {
    if (!this.newRetention.countryName) {
      return;
    }

    const countryName = this.newRetention.countryName.trim();
    const isOtherCountry = countryName.toUpperCase() === 'OTRO' || 
                          countryName.toUpperCase() === 'OTHER' ||
                          !SUPPORTED_COUNTRIES.find((c: any) => c.name.toLowerCase() === countryName.toLowerCase());

    // Si es "OTRO" o no está en el listado y no tiene porcentaje o es 0, asignar 19% por defecto
    if (isOtherCountry && (!this.newRetention.percentage || this.newRetention.percentage === 0)) {
      this.newRetention.percentage = 19;
    }
  }

  // Para la gestión de vencimientos
  expiringCertificates: FiscalDocument[] = [];
  loadingExpiringCertificates: boolean = false;
  isCheckingCertificates: boolean = false;
  isUpdatingExpiredCertificates: boolean = false;
  isSendingReminder: boolean = false;
  
  // Estadísticas
  expiringCertificatesCount: number = 0;
  expiredCertificatesCount: number = 0;
  validCertificatesCount: number = 0;
  pendingCertificatesCount: number = 0;

  // Propiedades para cambios de residencia
  residenceChangeRequests: ResidenceChangeRequest[] = [];
  filteredResidenceChangeRequests: ResidenceChangeRequest[] = [];
  isLoadingResidenceChanges: boolean = false;
  
  // Filtros para cambios de residencia
  residenceStatusFilter: string = 'all';
  residenceUserFilter: string = '';
  residenceDateFilter: string = '';
  
  // Suscripción para cambios de residencia en tiempo real
  private residenceChangesSubscription?: Subscription;

  constructor(
    private afs: AngularFirestore,
    private fiscalDocService: FiscalDocumentsService,
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadDocuments();
    this.loadCountryRetentions();
    // Actualizar estados de cambios de residencia al cargar (sin mostrar mensaje si no hay cambios)
    this.updateResidenceChangeStatuses(false);
    
    // Si la pestaña activa es 'notifications', cargar las estadísticas
    if (this.activeTab === 'notifications') {
      this.loadExpiringCertificates();
      this.loadCertificateStatistics();
    }
  }

  ngOnDestroy(): void {
    // Limpiar suscripciones al destruir el componente
    if (this.residenceChangesSubscription) {
      this.residenceChangesSubscription.unsubscribe();
    }
  }

  setActiveTab(tabName: string): void {
    this.activeTab = tabName;
    
    // Limpiar suscripción anterior de cambios de residencia si existe
    if (this.residenceChangesSubscription) {
      this.residenceChangesSubscription.unsubscribe();
      this.residenceChangesSubscription = undefined;
    }
    
    // Recargar los datos correspondientes al tab
    if (tabName === 'documents') {
      this.loadDocuments();
    } else if (tabName === 'retentions') {
      this.loadCountryRetentions();
    } else if (tabName === 'notifications') {
      this.loadExpiringCertificates();
      this.loadCertificateStatistics();
    } else if (tabName === 'residenceChanges') {
      this.loadResidenceChangeRequests();
    }
  }

  loadCountryRetentions(): void {
    this.isLoadingRetentions = true;
    
    // Cargar retenciones de la colección 'retentions'
    this.afs.collection('retentions').get().pipe(take(1)).subscribe({
      next: (snapshot) => {
        try {
          const retentions: CountryRetention[] = [];
          
          snapshot.forEach(doc => {
            const data = doc.data() as any;
            
            // Verificar si el documento tiene la estructura esperada
            if (data && typeof data === 'object') {
              // Si el documento contiene un objeto con countryName y percentage
              if (data.countryName && data.percentage !== undefined) {
                retentions.push({
                  id: doc.id,
                  countryCode: data.countryCode || '',
                  countryName: data.countryName,
                  percentage: data.percentage
                });
              } else {
                // Si el documento contiene directamente los campos de países (un doc, varios países)
                Object.keys(data).forEach(countryName => {
                  const percentage = data[countryName];
                  if (typeof percentage === 'number') {
                    retentions.push({
                      id: `${doc.id}_${countryName}`,
                      docId: doc.id,
                      countryCode: countryName.substring(0, 2).toUpperCase(),
                      countryName: countryName,
                      percentage: percentage
                    });
                  }
                });
              }
            }
          });
          
          // Ordenar por nombre de país
          retentions.sort((a, b) => a.countryName.localeCompare(b.countryName));
          
          this.countryRetentions = retentions;
        } catch (error) {
          console.error('Error processing retentions:', error);
          this.countryRetentions = [];
        } finally {
          this.isLoadingRetentions = false;
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('Error loading retentions:', error);
        this.isLoadingRetentions = false;
        this.countryRetentions = [];
        this.cdr.detectChanges();
      }
    });
  }

  loadDocuments(): void {
    this.isLoading = true;
    
    // Usar el servicio para cargar todos los documentos
    // Usar take(1) para tomar solo el primer valor y completar la suscripción
    this.fiscalDocService.getAllDocuments().pipe(take(1)).subscribe({
      next: (docs: any[]) => {
        try {
          // Manejar el caso cuando docs es null o undefined
          if (!docs || !Array.isArray(docs)) {
            this.documents = [];
            this.filteredDocuments = [];
            this.isLoading = false;
            return;
          }

          // Asegurarse de que cada documento tenga los campos necesarios
          this.documents = docs.map(doc => {
            // Verificar si el documento tiene un ID
            if (!doc || !doc.id) {
              return null;
            }
            
            const processedDoc = {
              ...doc,
              // Asegurar que estos campos siempre tengan un valor
              status: doc.status || 'pending',
              // Si no hay retentionPercentage, calcularlo basándose en countryOfResidence
              retentionPercentage: doc.retentionPercentage !== undefined && doc.retentionPercentage !== null 
                ? doc.retentionPercentage 
                : undefined, // No asignar 19 por defecto aquí, se calculará después si es necesario
              countryOfResidence: doc.countryOfResidence || 'No especificado',
              fileName: doc.fileName || 'Sin nombre',
              userEmail: doc.userEmail || 'No disponible',
              downloadUrl: doc.downloadUrl || '',
              uploadDate: doc.uploadDate || new Date()
            };
            
            return processedDoc;
          })
          // Filtrar documentos sin ID para evitar errores posteriores
          .filter(doc => doc !== null);
          
          // Asegurarse de que statusFilter esté inicializado correctamente
          if (!this.statusFilter) {
            this.statusFilter = 'all';
          }
          
          // Aplicar filtros después de asignar los documentos
          // Asegurarse de que los documentos estén asignados antes de aplicar filtros
          // Incluir certificados de residencia (fuera de España) para que caducados/por caducar aparezcan aquí
          this.mergeResidenceCertificatesIntoDocuments();
        } catch (error) {
          console.error('Error processing documents:', error);
          this.documents = [];
          this.filteredDocuments = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('Error loading documents:', error);
        this.isLoading = false;
        this.documents = [];
        this.filteredDocuments = [];
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Carga certificados de residencia (approved, expiring_soon, expired) y los fusiona en la lista de Documentación Fiscal.
   * Así los certificados caducados fuera de España se muestran en Documentación Fiscal, no solo en Residencia.
   */
  private mergeResidenceCertificatesIntoDocuments(): void {
    from(
      this.afs.collection('residence-change-requests', ref =>
        ref.where('status', 'in', ['approved', 'expiring_soon', 'expired'])).get()
    ).pipe(take(1)).subscribe({
        next: (snapshot) => {
          const converted = snapshot.docs.map(docSnap => {
            const d = docSnap.data() as any;
            const id = docSnap.id;
            return {
              id,
              userId: d.userId,
              fileName: `Certificado residencia a ${d.requestedCountry || 'N/A'}`,
              status: d.status || 'approved',
              countryOfResidence: d.requestedCountry || 'No especificado',
              retentionPercentage: d.requestedCountryRetentionPercentage,
              expiryDate: d.validityDate,
              uploadDate: d.requestDate,
              fileUrl: d.documentUrl || '',
              downloadUrl: d.documentUrl || '',
              userEmail: d.userEmail || 'No disponible',
              userName: d.userName,
              documentType: 'residence_change' as const,
              currentCountry: d.currentCountry,
              requestedCountryRetentionPercentage: d.requestedCountryRetentionPercentage,
              currentAddress: d.currentAddress,
              requestedAddress: d.requestedAddress
            };
          });
          this.documents = [...this.documents, ...converted];
          if (this.documents && this.documents.length >= 0) {
            this.applyFilters();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading residence certificates for documents:', err);
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  applyFilters(): void {
    // Asegurarse de que documents esté inicializado
    if (!this.documents || !Array.isArray(this.documents)) {
      this.filteredDocuments = [];
      return;
    }
    
    // Crear una copia del array para evitar problemas de referencia
    let filtered = [...this.documents];
    
    // Filtrar por estado
    if (this.statusFilter && this.statusFilter !== 'all') {
      if (this.statusFilter === 'expiring_soon') {
        // Para expiring_soon, incluir documentos con estado expiring_soon O documentos aprobados próximos a caducar
        filtered = filtered.filter(doc => {
          if (!doc) return false;
          // Si tiene estado expiring_soon, incluirlo
          if (doc.status === 'expiring_soon') {
            return true;
          }
          // Si tiene estado approved o change_approved y está próximo a caducar, incluirlo
          if ((doc.status === 'approved' || doc.status === 'change_approved') && this.isExpiringWithin30Days(doc)) {
            return true;
          }
          return false;
        });
      } else {
        filtered = filtered.filter(doc => doc && doc.status === this.statusFilter);
      }
    }
    
    // Filtrar por usuario/email
    if (this.userFilter && this.userFilter.trim() !== '') {
      const userFilterLower = this.userFilter.toLowerCase().trim();
      filtered = filtered.filter(doc => {
        if (!doc) return false;
        const email = (doc.userEmail || '').toLowerCase();
        const userName = (doc.userName || '').toLowerCase();
        return email.includes(userFilterLower) || userName.includes(userFilterLower);
      });
    }
    
    // Filtrar por fecha de subida
    if (this.dateFilter && this.dateFilter.trim() !== '') {
      const filterDate = new Date(this.dateFilter);
      filterDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(filterDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      filtered = filtered.filter(doc => {
        if (!doc || !doc.uploadDate) return false;
        try {
          const uploadDate = this.convertFirestoreDate(doc.uploadDate);
          uploadDate.setHours(0, 0, 0, 0);
          return uploadDate >= filterDate && uploadDate < nextDay;
        } catch (error) {
          return false;
        }
      });
    }
    
    this.filteredDocuments = filtered;
    // Forzar detección de cambios después de aplicar filtros
    this.cdr.detectChanges();
  }
  
  applyResidenceFilters(): void {
    // Asegurarse de que residenceChangeRequests esté inicializado
    if (!this.residenceChangeRequests || !Array.isArray(this.residenceChangeRequests)) {
      this.filteredResidenceChangeRequests = [];
      return;
    }
    
    // Crear una copia del array para evitar problemas de referencia
    let filtered = [...this.residenceChangeRequests];
    
    // Filtrar por estado
    if (this.residenceStatusFilter && this.residenceStatusFilter !== 'all') {
      filtered = filtered.filter(request => {
        if (!request) return false;
        return request.status === this.residenceStatusFilter;
      });
    }
    
    // Filtrar por usuario/email
    if (this.residenceUserFilter && this.residenceUserFilter.trim() !== '') {
      const userFilterLower = this.residenceUserFilter.toLowerCase().trim();
      filtered = filtered.filter(request => {
        if (!request) return false;
        const email = (request.userEmail || '').toLowerCase();
        const userName = (request.userName || '').toLowerCase();
        return email.includes(userFilterLower) || userName.includes(userFilterLower);
      });
    }
    
    // Filtrar por fecha de solicitud
    if (this.residenceDateFilter && this.residenceDateFilter.trim() !== '') {
      const filterDate = new Date(this.residenceDateFilter);
      filterDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(filterDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      filtered = filtered.filter(request => {
        if (!request || !request.requestDate) return false;
        try {
          const requestDate = request.requestDate?.toDate 
            ? request.requestDate.toDate() 
            : new Date(request.requestDate);
          requestDate.setHours(0, 0, 0, 0);
          return requestDate >= filterDate && requestDate < nextDay;
        } catch (error) {
          return false;
        }
      });
    }
    
    this.filteredResidenceChangeRequests = filtered;
  }

  filterResidenceByStatus(status: string): void {
    this.residenceStatusFilter = status;
    // Asegurarse de que las solicitudes estén cargadas antes de aplicar filtros
    if (this.residenceChangeRequests && this.residenceChangeRequests.length > 0) {
      this.applyResidenceFilters();
    } else {
      // Si no hay solicitudes cargadas, recargar
      this.loadResidenceChangeRequests();
    }
  }

  filterByStatus(status: string): void {
    this.statusFilter = status;
    // Asegurarse de que los documentos estén cargados antes de aplicar filtros
    if (this.documents && this.documents.length > 0) {
      this.applyFilters();
    } else {
      // Si no hay documentos cargados, recargar
      this.loadDocuments();
    }
  }

  // Verificar si un documento expira dentro de 30 días
  isExpiringWithin30Days(doc: any): boolean {
    if (!doc.expiryDate) return false;
    
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Manejar diferentes formatos de fecha (Timestamp de Firestore o Date)
      let expiryDate: Date;
      if (typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate) {
        expiryDate = (doc.expiryDate as any).toDate();
      } else {
        expiryDate = new Date(doc.expiryDate);
      }
      expiryDate.setHours(0, 0, 0, 0);
      
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      thirtyDaysFromNow.setHours(23, 59, 59, 999);

      // Debe estar en el futuro pero dentro de 30 días
      return expiryDate > today && expiryDate <= thirtyDaysFromNow;
    } catch (error) {
      return false;
    }
  }

  async approveDocument(doc: any): Promise<void> {
    // Validar que el documento tenga ID antes de continuar
    if (!doc || !doc.id) {
      Swal.fire('Error', 'No se puede aprobar el documento: ID de documento inválido.', 'error');
      return;
    }
    
    // Obtener el país del documento y el porcentaje correcto
    let userCountry = doc.countryOfResidence || '';
    
    // Obtener automáticamente el porcentaje correcto según el país
    let currentRetention = 19; // Por defecto
    if (userCountry) {
      try {
        currentRetention = await this.getCountryRetentionPercentage(userCountry);
      } catch (error) {
        // Error al obtener porcentaje automático, usar el por defecto
      }
    }

    Swal.fire({
      title: 'Aprobar documento',
      html: `
        <div class="mb-3" style="text-align: center;">
          <label class="form-label" style="text-align: center; display: block; width: 100%; margin-bottom: 0.5rem; font-weight: 500;">País de residencia fiscal</label>
          <input id="country" type="text" class="form-control" placeholder="Ej. España" value="${userCountry}" readonly style="text-align: center; margin: 0 auto; display: block; width: 80%; max-width: 250px;">
          <small class="form-text text-muted" style="text-align: center; display: block; width: 100%; margin-top: 0.25rem;">País detectado del documento subido</small>
        </div>
        <div class="mb-3" style="text-align: center;">
          <label class="form-label" style="text-align: center; display: block; width: 100%; margin-bottom: 0.5rem; font-weight: 500;">Porcentaje de retención (%)</label>
          <input id="retention" type="number" class="form-control" min="0" max="100" step="0.01" value="${currentRetention}" readonly style="text-align: center; margin: 0 auto; display: block; width: 80%; max-width: 250px;">
          <small class="form-text text-success" style="text-align: center; display: block; width: 100%; margin-top: 0.25rem;">✓ Porcentaje obtenido automáticamente para ${userCountry}</small>
        </div>
        <div class="mb-3" style="text-align: center;">
          <label class="form-label" style="text-align: center; display: block; width: 100%; margin-bottom: 0.5rem; font-weight: 500;">Fecha de vencimiento del certificado</label>
          <input id="expiryDate" type="date" class="form-control" style="text-align: center; margin: 0 auto; display: block; width: 80%; max-width: 250px;">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Aprobar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545',
      didOpen: () => {
        // Aplicar estilos adicionales después de que se abra el modal
        setTimeout(() => {
          const labels = document.querySelectorAll('.swal2-html-container label');
          const inputs = document.querySelectorAll('.swal2-html-container input');
          const smalls = document.querySelectorAll('.swal2-html-container small');
          
          labels.forEach(label => {
            (label as HTMLElement).style.textAlign = 'center';
            (label as HTMLElement).style.display = 'block';
            (label as HTMLElement).style.width = '100%';
            (label as HTMLElement).style.marginBottom = '0.5rem';
            (label as HTMLElement).style.fontWeight = '500';
          });
          
          inputs.forEach(input => {
            (input as HTMLElement).style.textAlign = 'center';
            (input as HTMLElement).style.margin = '0 auto';
            (input as HTMLElement).style.display = 'block';
            (input as HTMLElement).style.width = '80%';
            (input as HTMLElement).style.maxWidth = '250px';
          });
          
          smalls.forEach(small => {
            (small as HTMLElement).style.textAlign = 'center';
            (small as HTMLElement).style.display = 'block';
            (small as HTMLElement).style.width = '100%';
            (small as HTMLElement).style.marginTop = '0.25rem';
          });
        }, 100);
      },
      preConfirm: () => {
        const retention = parseFloat((document.getElementById('retention') as HTMLInputElement).value);
        const country = (document.getElementById('country') as HTMLInputElement).value;
        const expiryDateStr = (document.getElementById('expiryDate') as HTMLInputElement).value;
        
        // Permitir 0 como valor válido (algunos países tienen 0% de retención)
        if (isNaN(retention) || retention < 0 || retention > 100) {
          Swal.showValidationMessage('El porcentaje debe ser un número entre 0 y 100');
          return false;
        }
        
        if (!country.trim()) {
          Swal.showValidationMessage('El país de residencia fiscal es obligatorio');
          return false;
        }
        
        if (!expiryDateStr) {
          Swal.showValidationMessage('La fecha de vencimiento es obligatoria');
          return false;
        }
        
        const expiryDate = new Date(expiryDateStr);
        if (expiryDate <= new Date()) {
          Swal.showValidationMessage('La fecha de vencimiento debe ser posterior a la fecha actual');
          return false;
        }
        
        return { retention, country, expiryDate };
      }
    }).then(result => {
      if (result.isConfirmed && result.value) {
        const { retention, country, expiryDate } = result.value as { 
          retention: number; 
          country: string; 
          expiryDate: Date 
        };
        
        // Aprobar el documento con fecha de expiración usando el servicio
        this.fiscalDocService.approveDocumentWithExpiry(
          doc.id, 
          retention, 
          expiryDate,
          country
        ).subscribe(
          async () => {
            // Actualizar el porcentaje de retención y país de residencia del usuario
            try {
              const userDoc = await this.afs.collection('users').doc(doc.userId).get().toPromise();
              
              if (userDoc && userDoc.exists) {
                const userData = userDoc.data() as any;
                
                // Construir el objeto de actualización según el tipo de usuario
                const updateData = this.buildResidenceChangeUpdateData(
                  userData,
                  country,
                  null, // No hay dirección en documentos fiscales
                  retention
                );
                
                // Actualizar el usuario
                await this.afs.collection('users').doc(doc.userId).update(updateData);
                
                console.log(`✅ Usuario actualizado: país=${country}, retención=${retention}%`);
                
                Swal.fire(
                  'Documento aprobado',
                  `El documento ha sido aprobado con ${retention}% de retención (${country}) hasta ${expiryDate.toLocaleDateString()}. El perfil del usuario ha sido actualizado.`,
                  'success'
                );
              } else {
                // Si no existe el usuario, solo actualizar retención
                this.fiscalDocService.updateUserRetentionPercentage(doc.userId, retention)
                  .subscribe(
                    () => {
                      Swal.fire(
                        'Documento aprobado',
                        `El documento ha sido aprobado con ${retention}% de retención (${country}) hasta ${expiryDate.toLocaleDateString()}.`,
                        'success'
                      );
                    },
                    error => {
                      Swal.fire(
                        'Documento aprobado',
                        `El documento ha sido aprobado con ${retention}% de retención (${country}) hasta ${expiryDate.toLocaleDateString()}.`,
                        'success'
                      );
                    }
                  );
              }
              
              this.loadDocuments(); // Recargar documentos después de aprobar
            } catch (error: any) {
              console.error('Error al actualizar usuario:', error);
              // Si hay un error, intentar solo actualizar retención
              this.fiscalDocService.updateUserRetentionPercentage(doc.userId, retention)
                .subscribe(
                  () => {
                    Swal.fire(
                      'Documento aprobado',
                      `El documento ha sido aprobado con ${retention}% de retención (${country}) hasta ${expiryDate.toLocaleDateString()}.`,
                      'success'
                    );
                    this.loadDocuments();
                  },
                  updateError => {
                    Swal.fire(
                      'Advertencia',
                      'El documento fue aprobado, pero no se pudo actualizar el perfil del usuario.',
                      'warning'
                    );
                    this.loadDocuments();
                  }
                );
            }
          },
          error => {
            Swal.fire(
              'Error',
              'No se pudo aprobar el documento.',
              'error'
            );
          }
        );
      }
    });
  }

  /**
   * Rechaza un certificado (aportar certificado).
   * Importante: NO debe cambiarse el retentionPercentage del usuario al rechazar un certificado;
   * debe mantenerse el % que ya tenía aceptado. Solo al rechazar un *cambio de residencia* se aplica General.
   */
  rejectDocument(doc: any): void {
    // Validar que el documento tenga ID antes de continuar
    if (!doc || !doc.id) {
      Swal.fire('Error', 'No se puede rechazar el documento: ID de documento inválido.', 'error');
      return;
    }
    
    Swal.fire({
      title: 'Rechazar documento',
      text: 'Introduce el motivo del rechazo:',
      input: 'textarea',
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      inputValidator: (value) => {
        if (!value) {
          return 'Debes introducir un motivo';
        }
        return null;
      }
    }).then(result => {
      if (result.isConfirmed) {
        const userId = doc?.userId;
        // Preservar retención actual del usuario antes de rechazar (no cambiar a General en rechazo de certificado)
        const preserveRetention$ = userId
          ? from(this.afs.collection('users').doc(userId).get().toPromise()).pipe(take(1))
          : from([undefined]).pipe(take(1));
        preserveRetention$.subscribe(userSnap => {
          const userData = userSnap?.data() as { particular?: { retentionPercentage?: number }; Empresa?: { Datos_Empresa?: { retentionPercentage?: number } }; retentionPercentage?: number } | undefined;
          const currentRetention = userData?.Empresa?.Datos_Empresa?.retentionPercentage ?? userData?.retentionPercentage ?? userData?.particular?.retentionPercentage;
          const retentionToKeep = typeof currentRetention === 'number' && !isNaN(currentRetention)
            ? currentRetention
            : undefined;

          this.fiscalDocService.rejectDocument(doc.id, result.value)
            .subscribe(
              () => {
                // Reaplicar la retención que tenía el usuario para que no quede en General (p. ej. si una Cloud Function la cambió)
                if (userId && retentionToKeep !== undefined) {
                  this.fiscalDocService.updateUserRetentionPercentage(userId, retentionToKeep)
                    .pipe(take(1))
                    .subscribe({
                      next: () => {},
                      error: () => {}
                    });
                }
                Swal.fire(
                  'Documento rechazado',
                  'El documento ha sido rechazado correctamente.',
                  'success'
                );
                this.loadDocuments(); // Recargar documentos después de rechazar
              },
              error => {
                Swal.fire(
                  'Error',
                  'No se pudo rechazar el documento.',
                  'error'
                );
              }
            );
        });
      }
    });
  }

  viewUserProfile(userId: string): void {
    // Obtener información del usuario
    this.afs.collection('users').doc(userId).get().subscribe(userDoc => {
      if (userDoc.exists) {
        const userData = userDoc.data() as any;
        
        // Manejar posibles valores nulos o undefined
        const nombre = userData?.particular?.nombre || 'No disponible';
        const apellido = userData?.particular?.apellido1 || '';
        const email = userData?.email || 'No disponible';
        const pais = userData?.particular?.pais_de_residencia || 'No disponible';
        const kycStatus = userData?.kyc?.result == 1 ? 'Aprobado' : 'Pendiente';
        // retentionPercentage: en raíz si particular, en Empresa.Datos_Empresa si empresa
        const retentionVal = userData?.Empresa?.Datos_Empresa?.retentionPercentage ?? userData?.retentionPercentage ?? userData?.particular?.retentionPercentage;
        const retencion = retentionVal !== undefined && retentionVal !== null ? `${retentionVal}%` : 'No establecido';
        
        Swal.fire({
          title: 'Información del usuario',
          html: `
            <p><strong>Nombre:</strong> ${nombre} ${apellido}</p>
            <p><strong>Email:</strong> ${email}</p>
            <p><strong>UID:</strong> ${userId}</p>
            <p><strong>País:</strong> ${pais}</p>
            <p><strong>KYC:</strong> ${kycStatus}</p>
            <p><strong>% Retención actual:</strong> ${retencion}</p>
          `,
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#007bff'
        });
      } else {
        Swal.fire('Error', 'No se encontró información del usuario', 'error');
      }
    });
  }


  getStatusLabel(status: string): string {
    if (!status) {
      return 'Pendiente';
    }
    
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'Rechazado';
      case 'expired': return 'Caducado';
      case 'expiring_soon': return 'Actualizar';
      case 'change_requested': return 'Solicitud de cambio';
      case 'change_approved': return 'Cambio aprobado';
      case 'change_rejected': return 'Cambio rechazado';
      default: return 'Pendiente';
    }
  }

  formatDate(date: any): string {
    if (!date) return '-';
    if (typeof date === 'object' && date.toDate) {
      return date.toDate().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  getDaysUntilExpiry(document: any): number {
    if (!document.expiryDate) return 0;

    let expiryDate: Date;
    if (typeof document.expiryDate === 'object' && document.expiryDate && 'toDate' in document.expiryDate) {
      expiryDate = (document.expiryDate as any).toDate();
    } else if (document.expiryDate instanceof Date) {
      expiryDate = document.expiryDate;
    } else {
      expiryDate = new Date(document.expiryDate as unknown as string | number);
    }

    const currentDate = new Date();
    // Establecer hora a medianoche para comparar solo fechas
    currentDate.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    
    const timeDiff = expiryDate.getTime() - currentDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  showRejectionReason(doc: any): void {
    const rejectionDateValue = doc.rejectionDate ? this.convertFirestoreDate(doc.rejectionDate) : null;
    const rejectionDate = rejectionDateValue ? this.formatDate(rejectionDateValue) : 'No disponible';
    
    Swal.fire({
      title: 'Información del rechazo',
      html: `
        <div class="text-start">
          <p><strong>Motivo:</strong></p>
          <p class="mb-3">${doc.rejectionReason || 'No se especificó un motivo'}</p>
          <p><strong>Fecha de rechazo:</strong> ${rejectionDate}</p>
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#007bff',
      width: 'auto',
      customClass: {
        htmlContainer: 'text-start'
      }
    });
  }

  // Métodos para la gestión de vencimientos
  
  loadExpiringCertificates(): void {
    this.loadingExpiringCertificates = true;
    
    // Obtener fecha actual y fecha 30 días después
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalizar a medianoche
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    thirtyDaysFromNow.setHours(23, 59, 59, 999); // Fin del día
    
    // Cargar documentos fiscales que están próximos a caducar o ya caducaron
    this.afs
      .collection<FiscalDocument>('fiscal-documents', ref => 
        ref.where('status', 'in', ['approved', 'change_approved', 'expiring_soon', 'expired'])
          .orderBy('expiryDate'))
      .valueChanges({ idField: 'id' })
      .pipe(take(1))
      .subscribe(
        fiscalDocs => {
          // También cargar cambios de residencia (incluyendo expiring_soon y expired)
          this.afs
            .collection('residence-change-requests', ref => 
              ref.where('status', 'in', ['approved', 'expiring_soon', 'expired']))
            .valueChanges({ idField: 'id' })
            .pipe(take(1))
            .subscribe(
              residenceDocs => {
                
                // Convertir cambios de residencia al formato de documentos fiscales
                const convertedResidenceDocs = residenceDocs.map((doc: any) => ({
                  id: doc.id,
                  userId: doc.userId,
                  fileName: `Cambio de residencia a ${doc.requestedCountry}`,
                  expiryDate: doc.validityDate,
                  status: doc.status || 'change_approved',
                  userEmail: doc.userEmail,
                  userName: doc.userName,
                  uploadDate: doc.requestDate,
                  documentUrl: doc.documentUrl,
                  downloadUrl: doc.documentUrl, // Para compatibilidad con el HTML
                  country: doc.requestedCountry,
                  currentCountry: doc.currentCountry,
                  documentType: 'residence_change' as 'residence_change' // Marcar como cambio de residencia
                }));
                
                // Marcar documentos fiscales con su tipo
                const markedFiscalDocs = fiscalDocs.map(doc => ({
                  ...doc,
                  documentType: 'fiscal' as 'fiscal'
                }));
                
                // Combinar ambos tipos de documentos
                const allDocs: FiscalDocument[] = [...markedFiscalDocs, ...convertedResidenceDocs];
                
                // Actualizar estados de documentos según su fecha de caducidad y filtrar
                Promise.all(
                  allDocs.map(async doc => {
                    if (!doc.expiryDate) return null;

                    const daysUntilExpiry = this.getDaysUntilExpiry(doc);
                    let newStatus: string | null = null;
                    
                    // Si ya caducó (días negativos o 0)
                    if (daysUntilExpiry <= 0 && (doc.status === 'approved' || doc.status === 'change_approved')) {
                      newStatus = 'expired';
                    }
                    // Si queda 1 mes o menos para caducar (30 días)
                    else if (daysUntilExpiry > 0 && daysUntilExpiry <= 30 && (doc.status === 'approved' || doc.status === 'change_approved')) {
                      newStatus = 'expiring_soon';
                    }

                    // Si hay un cambio de estado, actualizar en la base de datos
                    if (newStatus && doc.id) {
                      try {
                        // Determinar en qué colección actualizar
                        const collection = doc.fileName?.includes('Cambio de residencia') 
                          ? 'residence-change-requests' 
                          : 'fiscal-documents';
                        
                        await this.afs.collection(collection).doc(doc.id).update({
                          status: newStatus,
                          lastStatusUpdate: new Date()
                        });
                        // Actualizar el estado en memoria
                        doc.status = newStatus as any;
                      } catch (error) {
                        // Error al actualizar estado del documento
                      }
                    }
                    
                    return doc;
                  })
                ).then(processedDocs => {
                  // Filtrar documentos nulos y luego aplicar el filtro de fecha
                  const validDocs = processedDocs.filter(doc => doc !== null) as FiscalDocument[];
                  
                  // Filtrar manualmente en el código
                  this.expiringCertificates = validDocs.filter(doc => {
                    if (!doc.expiryDate) {
                      return false;
                    }
                    try {
                      const expiryDate = typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate 
                        ? (doc.expiryDate as any).toDate() 
                        : new Date(doc.expiryDate as any);
                      
                      // Normalizar fecha de expiración a medianoche para comparación
                      expiryDate.setHours(0, 0, 0, 0);
                      
                      // Incluir documentos que:
                      // 1. Ya caducaron (fecha <= today)
                      // 2. Están próximos a caducar (today < fecha <= thirtyDaysFromNow)
                      // 3. Tienen estado expiring_soon o expired (por si acaso)
                      const isExpired = expiryDate <= today;
                      const isExpiringSoon = expiryDate > today && expiryDate <= thirtyDaysFromNow;
                      const hasExpiringStatus = doc.status === 'expiring_soon' || doc.status === 'expired';
                      
                      return isExpired || isExpiringSoon || hasExpiringStatus;
                    } catch (error) {
                      return false;
                    }
                  });
                  
                  this.loadingExpiringCertificates = false;
                  this.cdr.detectChanges();
                }).catch(error => {
                  console.error('Error processing documents:', error);
                  this.loadingExpiringCertificates = false;
                  this.cdr.detectChanges();
                });
              },
              error => {
                this.loadingExpiringCertificates = false;
                this.cdr.detectChanges();
              }
            );
          },
          error => {
            this.loadingExpiringCertificates = false;
            this.cdr.detectChanges();
            Swal.fire('Error', 'Could not load expiring certificates', 'error');
          }
      );
  }
  
  loadCertificateStatistics(): void {
    // Obtener recuento de certificados por caducar
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    thirtyDaysFromNow.setHours(23, 59, 59, 999); // Fin del día para incluir todo el día 30
    
    // Certificados por caducar (estado expiring_soon o próximos a caducar)
    // Incluye documentos fiscales y cambios de residencia
    // IMPORTANTE: No incluir los que ya están caducados (fecha <= today)
    this.afs
      .collection('fiscal-documents', ref => 
        ref.where('status', 'in', ['expiring_soon', 'approved', 'change_approved']))
      .get()
      .pipe(take(1))
      .subscribe(snapshot => {
        // Filtrar manualmente para contar solo los que están próximos a caducar (NO los ya caducados)
        let count = 0;
        snapshot.docs.forEach(doc => {
          const data = doc.data() as FiscalDocument;
          if (data.status === 'expiring_soon' && data.expiryDate) {
            // Verificar que no esté ya caducado
            try {
              const expiryDate = typeof data.expiryDate === 'object' && 'toDate' in data.expiryDate 
                ? (data.expiryDate as any).toDate() 
                : new Date(data.expiryDate as any);
              expiryDate.setHours(0, 0, 0, 0);
              // Solo contar si aún no ha caducado (fecha > today)
              if (expiryDate > today && expiryDate <= thirtyDaysFromNow) {
                count++;
              }
            } catch (error) {
              // Error processing expiry date
            }
          } else if ((data.status === 'approved' || data.status === 'change_approved') && data.expiryDate) {
            try {
              const expiryDate = typeof data.expiryDate === 'object' && 'toDate' in data.expiryDate 
                ? (data.expiryDate as any).toDate() 
                : new Date(data.expiryDate as any);
              expiryDate.setHours(0, 0, 0, 0);
              // Solo contar si está próximo a caducar pero aún no ha caducado
              if (expiryDate > today && expiryDate <= thirtyDaysFromNow) {
                count++;
              }
            } catch (error) {
              // Error processing expiry date
            }
          }
        });
        
        // También contar cambios de residencia próximos a caducar
        // Buscar tanto los que tienen status 'expiring_soon' como los aprobados próximos a caducar
        this.afs
          .collection('residence-change-requests', ref => 
            ref.where('status', 'in', ['expiring_soon', 'approved']))
          .get()
          .pipe(take(1))
          .subscribe(residenceSnapshot => {
            residenceSnapshot.docs.forEach(doc => {
              const data = doc.data() as any;
              if (data.validityDate) {
                try {
                  const validityDate = typeof data.validityDate === 'object' && 'toDate' in data.validityDate 
                    ? (data.validityDate as any).toDate() 
                    : new Date(data.validityDate as any);
                  validityDate.setHours(0, 0, 0, 0);
                  // Solo contar si está próximo a caducar pero aún no ha caducado
                  if (validityDate > today && validityDate <= thirtyDaysFromNow) {
                    count++;
                  }
                } catch (error) {
                  // Error processing residence validity date
                }
              }
            });
            this.expiringCertificatesCount = count;
            this.cdr.detectChanges();
          });
      });
    
    // Certificados caducados (incluyendo cambios de residencia)
    // Contar todos los documentos fiscales con fecha de caducidad pasada, independientemente del estado
    this.afs
      .collection('fiscal-documents', ref => 
        ref.where('status', 'in', ['approved', 'change_approved', 'expiring_soon', 'expired']))
      .get()
      .pipe(take(1))
      .subscribe(snapshot => {
        let expiredCount = 0;
        
        // Contar documentos fiscales caducados por fecha, no solo por estado
        snapshot.docs.forEach(doc => {
          const data = doc.data() as FiscalDocument;
          if (data.expiryDate) {
            try {
              const expiryDate = typeof data.expiryDate === 'object' && 'toDate' in data.expiryDate 
                ? (data.expiryDate as any).toDate() 
                : new Date(data.expiryDate as any);
              expiryDate.setHours(0, 0, 0, 0);
              // Si la fecha de caducidad es hoy o anterior, está caducado
              if (expiryDate <= today) {
                expiredCount++;
              }
            } catch (error) {
              // Error processing expiry date
            }
          } else if (data.status === 'expired') {
            // Si tiene estado expired pero no fecha, contarlo también
            expiredCount++;
          }
        });
        
        // También contar cambios de residencia caducados
        // Buscar tanto los que tienen status 'expired' como los aprobados con fecha caducada
        this.afs
          .collection('residence-change-requests', ref => 
            ref.where('status', 'in', ['expired', 'approved', 'expiring_soon']))
          .get()
          .pipe(take(1))
          .subscribe(residenceSnapshot => {
            residenceSnapshot.docs.forEach(doc => {
              const data = doc.data() as any;
              // Si tiene status expired, contarlo directamente
              if (data.status === 'expired') {
                expiredCount++;
              } else if ((data.status === 'approved' || data.status === 'expiring_soon') && data.validityDate) {
                // Si está aprobado o expiring_soon, verificar si ya caducó por fecha
                try {
                  const validityDate = typeof data.validityDate === 'object' && 'toDate' in data.validityDate 
                    ? (data.validityDate as any).toDate() 
                    : new Date(data.validityDate as any);
                  validityDate.setHours(0, 0, 0, 0);
                  if (validityDate <= today) {
                    expiredCount++;
                  }
                } catch (error) {
                  // Error processing residence validity date
                }
              }
            });
            this.expiredCertificatesCount = expiredCount;
            this.cdr.detectChanges();
          });
      });
    
    // Certificados vigentes (aprobados y no próximos a caducar)
    // Incluye documentos fiscales y cambios de residencia vigentes
    this.afs
      .collection('fiscal-documents', ref => 
        ref.where('status', 'in', ['approved', 'change_approved']))
      .get()
      .pipe(take(1))
      .subscribe(snapshot => {
        // Filtrar manualmente para contar solo los que no están próximos a caducar
        let count = 0;
        snapshot.docs.forEach(doc => {
          const data = doc.data() as FiscalDocument;
          if (data.expiryDate) {
            try {
              const expiryDate = typeof data.expiryDate === 'object' && 'toDate' in data.expiryDate 
                ? (data.expiryDate as any).toDate() 
                : new Date(data.expiryDate as any);
              expiryDate.setHours(0, 0, 0, 0);
              if (expiryDate > thirtyDaysFromNow) {
                count++;
              }
            } catch (error) {
              // Error processing expiry date
            }
          }
        });
        
        // También contar cambios de residencia vigentes
        this.afs
          .collection('residence-change-requests', ref => 
            ref.where('status', '==', 'approved'))
          .get()
          .pipe(take(1))
          .subscribe(residenceSnapshot => {
            residenceSnapshot.docs.forEach(doc => {
              const data = doc.data() as any;
              if (data.validityDate) {
                try {
                  const validityDate = typeof data.validityDate === 'object' && 'toDate' in data.validityDate 
                    ? (data.validityDate as any).toDate() 
                    : new Date(data.validityDate as any);
                  validityDate.setHours(0, 0, 0, 0);
                  if (validityDate > thirtyDaysFromNow) {
                    count++;
                  }
                } catch (error) {
                  // Error processing residence validity date
                }
              }
            });
            this.validCertificatesCount = count;
            this.cdr.detectChanges();
          });
      });
    
    // Certificados pendientes de revisión (incluyendo cambios de residencia)
    // Incluye documentos fiscales y cambios de residencia con status 'pending'
    this.afs
      .collection('fiscal-documents', ref => 
        ref.where('status', 'in', ['pending', 'change_requested']))
      .get()
      .pipe(take(1))
      .subscribe(snapshot => {
        let pendingCount = snapshot.size;
        
        // También contar cambios de residencia pendientes
        // Buscar todos los cambios de residencia con status 'pending'
        this.afs
          .collection('residence-change-requests', ref => 
            ref.where('status', '==', 'pending'))
          .get()
          .pipe(take(1))
          .subscribe(residenceSnapshot => {
            pendingCount += residenceSnapshot.size;
            this.pendingCertificatesCount = pendingCount;
            this.cdr.detectChanges();
          });
      });
  }

  checkExpiringCertificates(): void {
    this.isCheckingCertificates = true;
    
    Swal.fire({
      title: 'Enviando notificaciones',
      text: 'Enviando correos a usuarios con certificados próximos a caducar...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    this.fiscalDocService.checkExpiringCertificates()
      .pipe(take(1))
      .subscribe(
        () => {
          this.isCheckingCertificates = false;
          
          Swal.fire({
            title: 'Envío completado',
            text: 'Se han enviado las notificaciones a los usuarios con certificados próximos a caducar.',
            icon: 'success'
          });
          
          this.loadExpiringCertificates();
          this.loadCertificateStatistics();
        },
        error => {
          console.error('Error al enviar notificaciones:', error);
          this.isCheckingCertificates = false;
          
          Swal.fire({
            title: 'Error',
            text: 'Ha ocurrido un error al enviar las notificaciones: ' + error.message,
            icon: 'error'
          });
        }
      );
  }
  
  updateExpiredCertificates(): void {
    this.isUpdatingExpiredCertificates = true;
    
    Swal.fire({
      title: 'Enviando notificaciones',
      html: 'Actualizando estados y enviando notificaciones:<br><br>• Correos electrónicos a usuarios<br>• Notificaciones internas en Domoblock',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    this.fiscalDocService.checkAndUpdateExpiredDocuments()
      .pipe(take(1))
      .subscribe(
        () => {
          this.isUpdatingExpiredCertificates = false;
          
          Swal.fire({
            title: 'Envío completado',
            html: 'Se han actualizado los estados y enviado notificaciones a los usuarios con certificados caducados:<br><br>✓ Correos electrónicos enviados<br>✓ Notificaciones internas creadas',
            icon: 'success'
          });
          
          this.loadDocuments();
          this.loadExpiringCertificates();
          this.loadCertificateStatistics();
        },
        error => {
          console.error('Error al enviar notificaciones de caducidad:', error);
          this.isUpdatingExpiredCertificates = false;
          
          Swal.fire({
            title: 'Error',
            text: 'Ha ocurrido un error al enviar las notificaciones: ' + error.message,
            icon: 'error'
          });
        }
      );
  }
  
  sendReminderEmail(doc: FiscalDocument): void {
    if (!doc.userEmail) {
      Swal.fire('Error', 'Este usuario no tiene correo electrónico registrado.', 'error');
      return;
    }
    
    this.isSendingReminder = true;
    
    // Utilizamos el servicio para enviar correo personalizado
    const userName = doc.userName || 'Usuario';
    const documentName = doc.fileName;
    const documentType = (doc as any).documentType === 'residence_change' ? 'cambio de residencia' : 'documento fiscal';
    const expiryDate = typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate 
      ? (doc.expiryDate as any).toDate() 
      : new Date(doc.expiryDate as any);
    
    if (!expiryDate) {
      Swal.fire('Error', 'Este documento no tiene fecha de caducidad.', 'error');
      this.isSendingReminder = false;
      return;
    }
    
    this.fiscalDocService.sendExpiryNotification(doc.userEmail, userName, documentName, expiryDate)
      .then(() => {
        this.isSendingReminder = false;
        Swal.fire(
          'Recordatorio enviado',
          `Se ha enviado un recordatorio sobre el ${documentType} a ${doc.userEmail}.`,
          'success'
        );
      })
      .catch(error => {
        console.error('Error al enviar recordatorio:', error);
        this.isSendingReminder = false;
        Swal.fire(
          'Error',
          'No se pudo enviar el recordatorio.',
          'error'
        );
      });
  }
  
  getRemainingDays(expiryDate: Date): number {
    const today = new Date();
    const diffTime = expiryDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Método helper para convertir fechas de Firestore
  convertFirestoreDate(date: any): Date {
    if (date?.toDate) {
      return date.toDate();
    }
    return new Date(date);
  }

  // Método para editar datos de retención de un documento ya aprobado
  editRetentionData(doc: any): void {
    if (!doc || !doc.id) {
      console.error('Error: El documento no tiene ID', doc);
      Swal.fire('Error', 'No se pueden editar los datos: ID de documento inválido.', 'error');
      return;
    }
    // Certificado de residencia (fuera de España): extender fecha de validez
    if ((doc as any).documentType === 'residence_change') {
      this.afs.collection('residence-change-requests').doc(doc.id).get().toPromise()
        .then(snap => {
          if (!snap?.exists) {
            Swal.fire('Error', 'No se encontró la solicitud de residencia.', 'error');
            return Promise.resolve();
          }
          const data = snap.data() ?? {};
          const request: ResidenceChangeRequest = { id: snap.id, ...(data as object) } as ResidenceChangeRequest;
          return this.extendValidityDate(request).then(() => this.loadDocuments());
        })
        .catch(err => {
          console.error('Error al cargar solicitud de residencia:', err);
          Swal.fire('Error', 'No se pudo cargar la solicitud.', 'error');
        });
      return;
    }
    let userCountry = doc.countryOfResidence || '';
    const fromFirebase = this.getRetentionFromCountryRetentionsSync(userCountry);
    let retentionPercentage = doc.retentionPercentage ?? fromFirebase ?? 19;
    let expiryDate = doc.expiryDate ? doc.expiryDate.toDate() : new Date();
    
    // Formato de fecha para el campo date: YYYY-MM-DD
    const formatDateForInput = (date: Date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const formattedDate = formatDateForInput(expiryDate);
    
    Swal.fire({
      title: 'Editar datos de retención',
      html: `
        <div class="mb-3">
          <label class="form-label">Porcentaje de retención (%)</label>
          <input id="retention" type="number" class="form-control" min="0" max="100" step="0.01" value="${retentionPercentage}">
        </div>
        <div class="mb-3">
          <label class="form-label">País de residencia fiscal</label>
          <input id="country" type="text" class="form-control" value="${userCountry}">
        </div>
        <div class="mb-3">
          <label class="form-label">Fecha de vencimiento del certificado</label>
          <input id="expiryDate" type="date" class="form-control" value="${formattedDate}">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Actualizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545',
      preConfirm: () => {
        const retention = parseFloat((document.getElementById('retention') as HTMLInputElement).value);
        const country = (document.getElementById('country') as HTMLInputElement).value;
        const expiryDateStr = (document.getElementById('expiryDate') as HTMLInputElement).value;
        
        // Permitir 0 como valor válido (algunos países tienen 0% de retención)
        if (isNaN(retention) || retention < 0 || retention > 100) {
          Swal.showValidationMessage('El porcentaje debe ser un número entre 0 y 100');
          return false;
        }
        
        if (!country.trim()) {
          Swal.showValidationMessage('El país de residencia fiscal es obligatorio');
          return false;
        }
        
        if (!expiryDateStr) {
          Swal.showValidationMessage('La fecha de vencimiento es obligatoria');
          return false;
        }
        
        const newExpiryDate = new Date(expiryDateStr);
        if (newExpiryDate <= new Date()) {
          Swal.showValidationMessage('La fecha de vencimiento debe ser posterior a la fecha actual');
          return false;
        }
        
        return { retention, country, expiryDate: newExpiryDate };
      }
    }).then(result => {
      if (result.isConfirmed && result.value) {
        const { retention, country, expiryDate } = result.value as { 
          retention: number; 
          country: string; 
          expiryDate: Date 
        };
        
        // Actualizar el documento
        this.fiscalDocService.approveDocumentWithExpiry(
          doc.id, 
          retention, 
          expiryDate,
          country
        ).subscribe(
          async () => {
            // Actualizar el porcentaje de retención y país de residencia del usuario
            try {
              const userDoc = await this.afs.collection('users').doc(doc.userId).get().toPromise();
              
              if (userDoc && userDoc.exists) {
                const userData = userDoc.data() as any;
                
                // Construir el objeto de actualización según el tipo de usuario
                const updateData = this.buildResidenceChangeUpdateData(
                  userData,
                  country,
                  null, // No hay dirección en documentos fiscales
                  retention
                );
                
                // Actualizar el usuario
                await this.afs.collection('users').doc(doc.userId).update(updateData);
                
                Swal.fire(
                  'Datos actualizados',
                  `Los datos de retención han sido actualizados correctamente. El perfil del usuario ha sido actualizado.`,
                  'success'
                );
              } else {
                // Si no existe el usuario, solo actualizar retención
                this.fiscalDocService.updateUserRetentionPercentage(doc.userId, retention)
                  .subscribe(
                    () => {
                      Swal.fire(
                        'Datos actualizados',
                        `Los datos de retención han sido actualizados correctamente.`,
                        'success'
                      );
                    },
                    error => {
                      Swal.fire(
                        'Datos actualizados',
                        `Los datos de retención han sido actualizados correctamente.`,
                        'success'
                      );
                    }
                  );
              }
              
              this.loadDocuments();
            } catch (error: any) {
              console.error('Error al actualizar usuario:', error);
              // Si hay un error, intentar solo actualizar retención
              this.fiscalDocService.updateUserRetentionPercentage(doc.userId, retention)
                .subscribe(
                  () => {
                    Swal.fire(
                      'Advertencia',
                      'Los datos fueron actualizados, pero no se pudo actualizar el perfil del usuario.',
                      'warning'
                    );
                    this.loadDocuments();
                  },
                  updateError => {
                    Swal.fire(
                      'Advertencia',
                      'Los datos fueron actualizados, pero no se pudo actualizar el perfil del usuario.',
                      'warning'
                    );
                    this.loadDocuments();
                  }
                );
            }
          },
          error => {
            Swal.fire(
              'Error',
              'No se pudieron actualizar los datos.',
              'error'
            );
          }
        );
      }
    });
  }

  // Getters para los filtros de documentos
  get pendingDocumentsCount(): number {
    return this.documents.filter(d => d.status === 'pending').length;
  }

  get approvedDocumentsCount(): number {
    return this.documents.filter(d => d.status === 'approved').length;
  }

  get rejectedDocumentsCount(): number {
    return this.documents.filter(d => d.status === 'rejected').length;
  }

  get expiredDocumentsCount(): number {
    return this.documents.filter(d => d.status === 'expired').length;
  }

  // Método para reconsiderar un documento rechazado, por caducar o caducado
  reconsiderDocument(doc: any): void {
    if (!doc || !doc.id) {
      console.error('Error: El documento no tiene ID', doc);
      Swal.fire('Error', 'No se puede reconsiderar el documento: ID de documento inválido.', 'error');
      return;
    }
    // Certificado de residencia (fuera de España): delegar en renovar solicitud caducada
    if ((doc as any).documentType === 'residence_change') {
      this.afs.collection('residence-change-requests').doc(doc.id).get().toPromise()
        .then(snap => {
          if (!snap?.exists) {
            Swal.fire('Error', 'No se encontró la solicitud de residencia.', 'error');
            return Promise.resolve();
          }
          const data = snap.data() ?? {};
          const request: ResidenceChangeRequest = { id: snap.id, ...(data as object) } as ResidenceChangeRequest;
          return this.renewExpiredRequest(request).then(() => this.loadDocuments());
        })
        .catch(err => {
          console.error('Error al cargar solicitud de residencia:', err);
          Swal.fire('Error', 'No se pudo cargar la solicitud.', 'error');
        });
      return;
    }
    let title = 'Reconsiderar documento';
    let text = '¿Quieres volver a poner este documento en estado pendiente de revisión?';
    
    if (doc.status === 'expiring_soon') {
      title = 'Actualizar documento';
      text = '¿Quieres poner este documento en estado pendiente para que el usuario pueda subir una nueva versión antes de que caduque?';
    } else if (doc.status === 'expired') {
      title = 'Renovar documento caducado';
      text = '¿Quieres poner este documento en estado pendiente para que el usuario pueda subir una nueva versión?';
    }
    
    Swal.fire({
      title: title,
      text: text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, reconsiderar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745'
    }).then(result => {
      if (result.isConfirmed) {
        // Cambiar el estado a pendiente
        this.afs.collection('fiscal-documents').doc(doc.id).update({
          status: 'pending',
          rejectionReason: null, // Eliminar motivo de rechazo
          reviewDate: new Date() // Fecha actual como última revisión
        }).then(() => {
          Swal.fire(
            'Documento reconsiderado',
            'El documento ha vuelto a estado pendiente de revisión.',
            'success'
          );
          this.loadDocuments();
        }).catch(error => {
          console.error('Error al reconsiderar documento:', error);
          Swal.fire('Error', 'No se pudo reconsiderar el documento.', 'error');
        });
      }
    });
  }

  // Método para navegar a la pestaña de documentos con un filtro específico
  navigateToDocuments(status: string, checkExpiring: boolean = false): void {
    // Cambiar a la pestaña de documentos
    this.activeTab = 'documents';
    // Aplicar el filtro según el estado
    this.statusFilter = status;
    this.applyFilters();
    
    // Si se solicita filtrado adicional para certificados a caducar pronto, 
    // filtramos los documentos manualmente después de aplicar el filtro de estado
    if (checkExpiring && status === 'approved') {
      this.filteredDocuments = this.filteredDocuments.filter(doc => this.isExpiringWithin30Days(doc));
    }
  }

  async approveChangeRequest(doc: any): Promise<void> {
    if (!doc || !doc.id) {
      console.error('Error: El documento no tiene ID', doc);
      Swal.fire('Error', 'No se puede aprobar el cambio: ID de documento inválido.', 'error');
      return;
    }

    try {
      // Calcular fecha por defecto (1 año desde hoy)
      const defaultDate = new Date();
      defaultDate.setFullYear(defaultDate.getFullYear() + 1);
      const defaultDateString = defaultDate.toISOString().split('T')[0];

      const result = await Swal.fire({
        title: 'Aprobar solicitud de cambio',
        html: `
          <p>¿Estás seguro de que deseas aprobar esta solicitud de cambio?</p>
          <br>
          <div class="mb-3">
            <label for="expiryDate" class="form-label"><strong>Fecha de validez:</strong></label>
            <input type="date" id="expiryDate" class="swal2-input" value="${defaultDateString}" min="${new Date().toISOString().split('T')[0]}">
            <div class="text-muted" style="font-size: 0.875rem; margin-top: 0.5rem;">
              Selecciona hasta cuándo será válido este certificado fiscal.
            </div>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, aprobar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
        preConfirm: () => {
          const expiryDateInput = document.getElementById('expiryDate') as HTMLInputElement;
          const expiryDate = expiryDateInput.value;
          
          if (!expiryDate) {
            Swal.showValidationMessage('Por favor selecciona una fecha de validez');
            return false;
          }
          
          const selectedDate = new Date(expiryDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (selectedDate <= today) {
            Swal.showValidationMessage('La fecha de validez debe ser posterior a hoy');
            return false;
          }
          
          return expiryDate;
        }
      });

      if (result.isConfirmed && result.value) {
        // Convertir el string de fecha a objeto Date
        const expiryDate = new Date(result.value);
        await this.fiscalDocService.approveChangeRequestWithExpiry(doc.id, expiryDate);
        Swal.fire(
          '¡Aprobado!',
          'La solicitud de cambio ha sido aprobada con fecha de validez establecida.',
          'success'
        );
        this.loadDocuments(); // Recargar documentos
      }
    } catch (error: any) {
      console.error('Error al aprobar la solicitud de cambio:', error);
      let errorMessage = 'No se pudo aprobar la solicitud de cambio';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'El documento no fue encontrado';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  async rejectChangeRequest(doc: any): Promise<void> {
    if (!doc || !doc.id) {
      console.error('Error: El documento no tiene ID', doc);
      Swal.fire('Error', 'No se puede rechazar el cambio: ID de documento inválido.', 'error');
      return;
    }

    try {
      const result = await Swal.fire({
        title: 'Rechazar solicitud de cambio',
        text: 'Por favor, indica el motivo del rechazo:',
        input: 'textarea',
        inputPlaceholder: 'Escribe aquí el motivo...',
        showCancelButton: true,
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        inputValidator: (value) => {
          if (!value) {
            return 'Debes proporcionar un motivo para el rechazo';
          }
          return null;
        }
      });

      if (result.isConfirmed && result.value) {
        await this.fiscalDocService.rejectChangeRequest(doc.id, result.value);
        Swal.fire(
          '¡Rechazado!',
          'La solicitud de cambio ha sido rechazada.',
          'success'
        );
        this.loadDocuments(); // Recargar documentos
      }
    } catch (error: any) {
      console.error('Error al rechazar la solicitud de cambio:', error);
      let errorMessage = 'No se pudo rechazar la solicitud de cambio';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'El documento no fue encontrado';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  addCountryRetention(): void {
    // Verificar campos obligatorios
    if (!this.newRetention.countryName) {
      Swal.fire('Error', 'El nombre del país es obligatorio', 'error');
      return;
    }

    // Normalizar el nombre del país
    const countryName = this.newRetention.countryName.trim();
    const isOtherCountry = countryName.toUpperCase() === 'OTRO' || 
                          countryName.toUpperCase() === 'OTHER' ||
                          !SUPPORTED_COUNTRIES.find((c: any) => c.name.toLowerCase() === countryName.toLowerCase());

    // Si es "OTRO" o no está en el listado, asignar 19% por defecto si no se especificó un porcentaje
    let percentage = this.newRetention.percentage;
    if (isOtherCountry && (percentage === null || percentage === undefined || percentage === 0)) {
      percentage = 19;
    } else if (percentage === null || percentage === undefined) {
      Swal.fire('Error', 'El porcentaje de retención es obligatorio', 'error');
      return;
    }

    // Generar código del país si no está presente
    const countryCode = this.newRetention.countryCode || 
                       (countryName ? countryName.substring(0, 2).toUpperCase() : 'OT');
    
    if (!countryCode) {
      Swal.fire('Error', 'No se pudo generar un código para el país', 'error');
      return;
    }

    this.isLoadingRetentions = true;
    
    // Guardar en la colección 'retentions' con estructura de objeto
    const retentionData = {
      countryName: countryName,
      countryCode: countryCode,
      percentage: percentage
    };
    
    this.afs.collection('retentions').add(retentionData)
      .then(() => {
        Swal.fire(
          'Guardado',
          `Retención para ${countryName} guardada correctamente${isOtherCountry ? ' (19% asignado por defecto)' : ''}.`,
          'success'
        );
        // Limpiar el formulario
        this.newRetention = {
          countryCode: '',
          countryName: '',
          percentage: 0
        };
        // Recargar la lista
        this.loadCountryRetentions();
      })
      .catch(error => {
        console.error('Error saving retention:', error);
        Swal.fire(
          'Error',
          'No se pudo guardar la retención. Inténtalo de nuevo.',
          'error'
        );
        this.isLoadingRetentions = false;
        this.cdr.detectChanges();
      });
  }

  editCountryRetention(retention: CountryRetention): void {
    // Verificar si el país es "OTRO" o no está en el listado
    const countryName = retention.countryName || '';
    const isOtherCountry = countryName.toUpperCase() === 'OTRO' || 
                          countryName.toUpperCase() === 'OTHER' ||
                          !SUPPORTED_COUNTRIES.find((c: any) => c.name.toLowerCase() === countryName.toLowerCase());
    
    // Si es "OTRO" o no está en el listado y no tiene porcentaje, usar 19% por defecto
    const defaultPercentage = (isOtherCountry && (!retention.percentage || retention.percentage === 0)) ? 19 : retention.percentage;
    
    Swal.fire({
      title: 'Editar retención',
      html: `
        <div class="mb-3">
          <label class="form-label">País</label>
          <input id="countryName" type="text" class="form-control" value="${countryName}" readonly>
          ${isOtherCountry ? '<small class="form-text text-muted">País no listado - Puede editar el porcentaje</small>' : ''}
        </div>
        <div class="mb-3">
          <label class="form-label">Porcentaje de retención (%)</label>
          <input id="percentage" type="number" class="form-control" min="0" max="100" step="0.1" value="${defaultPercentage}">
          ${isOtherCountry ? '<small class="form-text text-info">Por defecto: 19% para países no listados</small>' : ''}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      preConfirm: () => {
        // Leer desde el popup del modal: en la página hay otro input id="percentage" en el formulario de retenciones
        const input = Swal.getPopup()?.querySelector('#percentage') as HTMLInputElement | null;
        const raw = input?.value ?? '';
        const percentage = parseFloat(raw);
        
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          Swal.showValidationMessage('El porcentaje debe ser un número entre 0 y 100');
          return false;
        }
        
        return percentage;
      }
    }).then(result => {
      if (result.isConfirmed && typeof result.value === 'number') {
        this.isLoadingRetentions = true;
        
        const docId = retention.docId || retention.id;
        const isMultiCountryDoc = !!retention.docId;
        const updateData = isMultiCountryDoc
          ? { [retention.countryName]: result.value }
          : { percentage: result.value };
        
        this.afs.collection('retentions').doc(docId).update(updateData)
          .then(() => {
            Swal.fire(
              'Actualizado',
              `Retención para ${countryName} actualizada correctamente.`,
              'success'
            );
            this.loadCountryRetentions();
          })
          .catch(error => {
            console.error('Error updating retention:', error);
            Swal.fire(
              'Error',
              'No se pudo actualizar la retención. Inténtalo de nuevo.',
              'error'
            );
            this.isLoadingRetentions = false;
            this.cdr.detectChanges();
          });
      }
    });
  }

  deleteCountryRetention(retention: CountryRetention): void {
    const countryName = retention.countryName || retention.id || 'País seleccionado';
    
    Swal.fire({
      title: '¿Estás seguro?',
      text: `Se eliminará la retención para ${countryName}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (result.isConfirmed) {
        this.isLoadingRetentions = true;
        
        const docId = retention.docId || retention.id;
        const isMultiCountryDoc = !!retention.docId;
        
        if (isMultiCountryDoc) {
          const docRef = this.afs.collection('retentions').doc(docId);
          docRef.get().pipe(take(1)).subscribe({
            next: (docSnap) => {
              if (!docSnap.exists) {
                this.isLoadingRetentions = false;
                this.loadCountryRetentions();
                this.cdr.detectChanges();
                return;
              }
              const data = docSnap.data() as Record<string, unknown>;
              const keys = Object.keys(data).filter(k => k !== retention.countryName);
              if (keys.length === 0) {
                docRef.delete()
                  .then(() => this.onRetentionDeleteSuccess(countryName))
                  .catch(err => this.onRetentionDeleteError(err));
              } else {
                docRef.update({ [retention.countryName]: firebase.firestore.FieldValue.delete() })
                  .then(() => this.onRetentionDeleteSuccess(countryName))
                  .catch(err => this.onRetentionDeleteError(err));
              }
            },
            error: (err) => this.onRetentionDeleteError(err)
          });
        } else {
          this.afs.collection('retentions').doc(docId).delete()
            .then(() => this.onRetentionDeleteSuccess(countryName))
            .catch(err => this.onRetentionDeleteError(err));
        }
      }
    });
  }

  private onRetentionDeleteSuccess(countryName: string): void {
    Swal.fire(
      'Eliminado',
      `Retención para ${countryName} eliminada correctamente.`,
      'success'
    );
    this.loadCountryRetentions();
    this.isLoadingRetentions = false;
    this.cdr.detectChanges();
  }

  private onRetentionDeleteError(error: unknown): void {
    console.error('Error deleting retention:', error);
    Swal.fire(
      'Error',
      'No se pudo eliminar la retención. Inténtalo de nuevo.',
      'error'
    );
    this.isLoadingRetentions = false;
    this.cdr.detectChanges();
  }

  /**
   * Obtiene la retención para mostrar en la tabla del admin.
   * Fuente de verdad: Firebase (doc.retentionPercentage guardado o countryRetentions).
   * No se usa porcentaje hardcodeado por país (p. ej. España debe venir de Firebase).
   */
  getDocumentRetentionForDisplay(doc: any): number {
    // Si el documento tiene retentionPercentage guardado, usarlo
    if (doc.retentionPercentage !== undefined && doc.retentionPercentage !== null) {
      return doc.retentionPercentage;
    }
    
    // Si no tiene retentionPercentage, buscar en countryRetentions (Firebase)
    if (doc.countryOfResidence && doc.countryOfResidence !== 'No especificado') {
      const retention = this.getRetentionFromCountryRetentionsSync(doc.countryOfResidence);
      if (retention !== undefined && retention !== null) {
        return retention;
      }
    }
    
    // Solo si no hay país o no está en Firebase: valor por defecto (evitar hardcode por país)
    return 19;
  }

  /** Busca el porcentaje de retención en countryRetentions (Firebase) por nombre de país. */
  private getRetentionFromCountryRetentionsSync(countryName: string): number | undefined {
    if (!countryName || !this.countryRetentions?.length) return undefined;
    const name = countryName.trim().toLowerCase();
    const r = this.countryRetentions.find((x: CountryRetention) => x.countryName?.toLowerCase() === name);
    return r?.percentage;
  }

  /** Para tablas: muestra porcentaje guardado o el de Firebase por país; si no hay ninguno, "—". */
  getRetentionDisplayValue(countryName: string, storedPercentage?: number): string {
    const n = storedPercentage ?? this.getRetentionFromCountryRetentionsSync(countryName ?? '');
    return n != null ? `${n}%` : '—';
  }

  // Método auxiliar para obtener el porcentaje de retención por país
  /**
   * Obtiene el porcentaje de retención desde Firebase (colección 'retentions').
   * No se usa porcentaje hardcodeado por país; España y el resto deben venir de Firebase.
   */
  private async getCountryRetentionPercentage(countryName: string): Promise<number> {
    try {
      if (!countryName || countryName.trim() === '') {
        return 19; // Solo cuando no hay país
      }

      const normalizedCountryName = countryName.trim();
      const lowerCountryName = normalizedCountryName.toLowerCase();
      const retentionsSnapshot = await this.afs.collection('retentions').get().toPromise();

      if (retentionsSnapshot && !retentionsSnapshot.empty) {
        for (const doc of retentionsSnapshot.docs) {
          const data = doc.data() as any;
          // Estructura 1: doc con countryName + percentage
          const docCountryName = (data.countryName || '').trim().toLowerCase();
          if (docCountryName === lowerCountryName) {
            if (data.percentage !== undefined && data.percentage !== null) {
              return data.percentage;
            }
          }
          // Estructura 2: un doc con varios países (clave = nombre país, valor = porcentaje)
          if (data[normalizedCountryName] !== undefined && typeof data[normalizedCountryName] === 'number') {
            return data[normalizedCountryName];
          }
          for (const key of Object.keys(data)) {
            if (key.trim().toLowerCase() === lowerCountryName && typeof data[key] === 'number') {
              return data[key];
            }
          }
        }
      }

      // Usar ya cargados en memoria si hay (misma fuente Firebase)
      const fromMemory = this.getRetentionFromCountryRetentionsSync(normalizedCountryName);
      if (fromMemory !== undefined && fromMemory !== null) {
        return fromMemory;
      }

      console.warn(`⚠️ No se encontró retención en Firebase para "${normalizedCountryName}", usando 19% por defecto`);
      return 19;
    } catch (error) {
      console.error(`❌ Error al buscar porcentaje de retención para ${countryName}:`, error);
      return 19;
    }
  }

  /**
   * Obtiene el porcentaje de retención General de Firebase (documento rechazado/caducado = aplicar retención general).
   */
  private async getGeneralRetentionPercentage(): Promise<number> {
    return this.getCountryRetentionPercentage('General');
  }

  // Método para aprobar una solicitud que había sido rechazada
  async approveRejectedRequest(request: ResidenceChangeRequest): Promise<void> {
    try {
      const result = await Swal.fire({
        title: '¿Aprobar solicitud rechazada?',
        html: `
          <p>¿Estás seguro de que deseas aprobar esta solicitud que había sido rechazada?</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'} (${request.userEmail || 'N/A'})</p>
          <p><strong>País actual:</strong> ${request.currentCountry || 'España'}</p>
          <p><strong>País solicitado:</strong> ${request.requestedCountry}</p>
          ${request.rejectionReason ? `<p><strong>Motivo anterior del rechazo:</strong> ${request.rejectionReason}</p>` : ''}
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, aprobar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745'
      });

      if (result.isConfirmed && request.id) {
        // Actualizar el estado de la solicitud
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          status: 'approved',
          approvalDate: firebase.firestore.Timestamp.now(),
          rejectionReason: null, // Limpiar el motivo de rechazo anterior
          rejectionDate: null
        });

        // Verificar si el usuario existe antes de intentar actualizarlo
        const userDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
        
        if (userDoc && userDoc.exists) {
          const userData = userDoc.data() as any;
          // Usar el porcentaje de retención que ya viene en la solicitud, o calcularlo como fallback
          let countryRetention: number;
          if (request.requestedCountryRetentionPercentage !== undefined && request.requestedCountryRetentionPercentage !== null) {
            countryRetention = request.requestedCountryRetentionPercentage;
            console.log('🔍 [DEBUG] approveRejectedRequest - Usando porcentaje de la solicitud:', countryRetention);
          } else {
            console.log('🔍 [DEBUG] approveRejectedRequest - Calculando porcentaje para:', request.requestedCountry);
            countryRetention = await this.getCountryRetentionPercentage(request.requestedCountry);
            console.log('🔍 [DEBUG] approveRejectedRequest - Porcentaje calculado:', countryRetention);
          }

          // Construir el objeto de actualización según el tipo de usuario (empresa o particular)
          const updateData = this.buildResidenceChangeUpdateData(
            userData,
            request.requestedCountry,
            request.requestedAddress,
            countryRetention
          );

          console.log('🔍 [DEBUG] approveRejectedRequest - Datos a actualizar:', updateData);
          await this.afs.collection('users').doc(request.userId).update(updateData);
          
          // Verificar que se actualizó correctamente
          const updatedUserDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
          const updatedUserData = updatedUserDoc?.data() as any;
          if (updatedUserData?.Empresa?.Datos_Empresa) {
            console.log('🔍 [DEBUG] approveRejectedRequest - particular.retentionPercentage después de actualizar:', updatedUserData.particular?.retentionPercentage);
          }
        } else {
                console.warn(`Usuario ${request.userId} no encontrado en la base de datos`);
        }

        const userExists = userDoc && userDoc.exists;
        Swal.fire(
          '¡Aprobado!',
          `La solicitud de cambio de residencia ha sido aprobada exitosamente.${userExists ? ' Los datos del usuario han sido actualizados.' : ' Nota: Solo se actualizó el estado de la solicitud.'}`,
          'success'
        );

        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al aprobar la solicitud:', error);
      let errorMessage = 'No se pudo aprobar la solicitud';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'La solicitud o el usuario no fue encontrado';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  // Método para eliminar completamente una solicitud
  async deleteResidenceChangeRequest(request: ResidenceChangeRequest): Promise<void> {
    try {
      const result = await Swal.fire({
        title: '¿Eliminar solicitud?',
        html: `
          <p>¿Estás seguro de que deseas eliminar permanentemente esta solicitud?</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'}</p>
          <p><strong>Estado:</strong> ${this.getResidenceChangeStatusLabel(request.status)}</p>
          <p class="text-danger"><strong>Esta acción no se puede deshacer.</strong></p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545'
      });

      if (result.isConfirmed && request.id) {
        await this.afs.collection('residence-change-requests').doc(request.id).delete();

        Swal.fire(
          'Eliminado',
          'La solicitud ha sido eliminada permanentemente',
          'success'
        );

        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error) {
      console.error('Error al eliminar la solicitud:', error);
      Swal.fire('Error', 'No se pudo eliminar la solicitud', 'error');
    }
  }

  // Método para mostrar el motivo del rechazo de cambio de residencia
  showResidenceRejectionReason(request: ResidenceChangeRequest): void {
    // Convertir la fecha de rechazo correctamente si es un Timestamp de Firestore
    const rejectionDateValue = request.rejectionDate ? this.convertFirestoreDate(request.rejectionDate) : null;
    const rejectionDate = rejectionDateValue ? this.formatDate(rejectionDateValue) : 'No disponible';
    
    Swal.fire({
      title: 'Motivo del rechazo',
      html: `
        <p><strong>Usuario:</strong> ${request.userName || 'N/A'}</p>
        <p><strong>Fecha de rechazo:</strong> ${rejectionDate}</p>
        <hr>
        <p><strong>Motivo:</strong></p>
        <div class="text-start" style="background-color: #f8f9fa; padding: 10px; border-radius: 5px; margin-top: 10px;">
          ${request.rejectionReason || 'No se proporcionó motivo'}
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Cerrar'
    });
  }

  // Método para rechazar una solicitud que había sido aprobada
  async rejectApprovedRequest(request: ResidenceChangeRequest): Promise<void> {
    try {
      const result = await Swal.fire({
        title: 'Rechazar solicitud aprobada',
        html: `
          <p>¿Estás seguro de que deseas rechazar esta solicitud que había sido aprobada?</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'} (${request.userEmail || 'N/A'})</p>
          <p><strong>País actual:</strong> ${request.currentCountry || 'España'}</p>
          <p><strong>País solicitado:</strong> ${request.requestedCountry}</p>
          <p class="text-warning"><strong>Nota:</strong> Esto revertirá los cambios en el perfil del usuario.</p>
        `,
        input: 'textarea',
        inputLabel: 'Motivo del rechazo',
        inputPlaceholder: 'Escribe el motivo del rechazo...',
        inputAttributes: {
          'aria-label': 'Motivo del rechazo'
        },
        showCancelButton: true,
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        inputValidator: (value) => {
          if (!value) {
            return 'Debes escribir un motivo para el rechazo';
          }
          return null;
        }
      });

      if (result.isConfirmed && request.id) {
        // Verificar si el usuario existe antes de intentar actualizarlo
        const userDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
        
        if (userDoc && userDoc.exists) {
          const userData = userDoc.data() as any;
          // Al rechazar: aplicar retención General de Firebase (no la del país actual)
          const generalRetention = await this.getGeneralRetentionPercentage();
          const revertData = this.buildResidenceChangeUpdateData(
            userData,
            request.currentCountry || 'España',
            request.currentAddress,
            generalRetention
          );
          await this.afs.collection('users').doc(request.userId).update(revertData);
        } else {
                console.warn(`Usuario ${request.userId} no encontrado en la base de datos`);
        }

        // Actualizar el estado de la solicitud
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          status: 'rejected',
          rejectionDate: firebase.firestore.Timestamp.now(),
          rejectionReason: result.value,
          approvalDate: null // Limpiar la fecha de aprobación
        });

        const userExists = userDoc && userDoc.exists;
        Swal.fire(
          'Rechazado',
          `La solicitud de cambio de residencia ha sido rechazada exitosamente.${userExists ? ' Los datos del usuario han sido revertidos.' : ' Nota: Solo se actualizó el estado de la solicitud.'}`,
          'success'
        );

        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al rechazar la solicitud aprobada:', error);
      let errorMessage = 'No se pudo rechazar la solicitud';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'La solicitud o el usuario no fue encontrado';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  // Métodos para cambios de residencia
  loadResidenceChangeRequests(): void {
    // Limpiar suscripción anterior si existe
    if (this.residenceChangesSubscription) {
      this.residenceChangesSubscription.unsubscribe();
    }
    
    this.isLoadingResidenceChanges = true;
    
    // Usar suscripción en tiempo real para actualizaciones automáticas
    this.residenceChangesSubscription = this.afs.collection<ResidenceChangeRequest>('residence-change-requests', ref =>
      ref.orderBy('requestDate', 'desc')
    ).valueChanges({ idField: 'id' })
    .subscribe({
      next: async requests => {
        try {
          console.log('🔍 [DEBUG] Admin - Solicitudes cargadas:', requests?.length || 0);
          
          // Manejar el caso cuando requests es null o undefined
          if (!requests || !Array.isArray(requests)) {
            this.residenceChangeRequests = [];
            this.filteredResidenceChangeRequests = [];
            this.isLoadingResidenceChanges = false;
            return;
          }
          
          // Enriquecer solicitudes con datos actuales del usuario si faltan
          const enrichedRequests = await Promise.all(
            requests.map(async (request) => {
              // Si faltan datos críticos, obtenerlos del usuario
              if (!request.currentCountry || !request.currentAddress || !request.userName) {
                try {
                  const userDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
                  if (userDoc && userDoc.exists) {
                    const userData = userDoc.data() as any;
                    
                    // Obtener país actual (empresa usa Pais_Fiscal, particular usa pais_de_residencia)
                    if (!request.currentCountry) {
                      request.currentCountry = 
                        userData?.Empresa?.Datos_Empresa?.Pais_Fiscal ||
                        userData?.Empresa?.Datos_Empresa?.pais_de_residencia ||
                        userData?.particular?.pais_de_residencia ||
                        userData?.Empresa?.Representante?.pais_de_residencia ||
                        'España';
                    }
                    
                    // Obtener dirección actual (empresa: Pais_Fiscal, Provincia, Codigo_Postal; particular: lowercase)
                    if (!request.currentAddress) {
                      const companyData = userData?.Empresa?.Datos_Empresa;
                      if (companyData) {
                        request.currentAddress = {
                          street: companyData.Razon_Social || companyData.domicilio || '',
                          locality: companyData.ciudad || '',
                          province: companyData.Provincia || companyData.CA_Estado || companyData.provincia || '',
                          postalCode: companyData.Codigo_Postal || companyData.codigo_postal || '',
                          country: companyData.Pais_Fiscal || companyData.pais_de_residencia || request.currentCountry
                        };
                      } else if (userData?.particular) {
                        request.currentAddress = {
                          street: userData.particular.domicilio || '',
                          locality: userData.particular.ciudad || '',
                          province: userData.particular.provincia || '',
                          postalCode: userData.particular.codigo_postal || '',
                          country: userData.particular.pais_de_residencia || request.currentCountry
                        };
                      }
                    }
                    
                    // Obtener nombre de usuario
                    if (!request.userName) {
                      // Prioridad: Nombre de empresa > Representante > Particular > displayName
                      if (userData?.Empresa?.Datos_Empresa?.Nombre_de_la_empresa) {
                        request.userName = userData.Empresa.Datos_Empresa.Nombre_de_la_empresa;
                      } else if (userData?.Empresa?.Representante) {
                        request.userName = `${userData.Empresa.Representante.nombre || ''} ${userData.Empresa.Representante.apellido1 || ''}`.trim();
                      } else if (userData?.particular) {
                        request.userName = `${userData.particular.nombre || ''} ${userData.particular.apellido1 || ''}`.trim();
                      } else {
                        request.userName = userData?.displayName || 'No disponible';
                      }
                    }
                    
                    // Obtener email si falta
                    if (!request.userEmail) {
                      request.userEmail = userData?.email || 'No disponible';
                    }
                  }
                } catch (error) {
                  console.error(`Error al enriquecer solicitud ${request.id}:`, error);
                }
              }
              return request;
            })
          );
          
          // Debuggear las primeras 3 solicitudes
          enrichedRequests.slice(0, 3).forEach((request, index) => {
            console.log(`🔍 [DEBUG] Admin - Solicitud ${index + 1}:`, {
              currentCountry: request.currentCountry,
              currentRetention: request.currentCountryRetentionPercentage,
              requestedCountry: request.requestedCountry,
              requestedRetention: request.requestedCountryRetentionPercentage
            });
          });
          
          this.residenceChangeRequests = enrichedRequests;
          this.applyResidenceFilters(); // Aplicar filtros después de cargar
        } catch (error) {
          console.error('Error processing residence change requests:', error);
          this.residenceChangeRequests = [];
          this.filteredResidenceChangeRequests = [];
        } finally {
          this.isLoadingResidenceChanges = false;
          // Forzar detección de cambios para asegurar que Angular actualice la vista
          this.cdr.detectChanges();
        }
      },
      error: error => {
        console.error('Error al cargar solicitudes:', error);
        this.residenceChangeRequests = [];
        this.filteredResidenceChangeRequests = [];
        this.isLoadingResidenceChanges = false;
        // Forzar detección de cambios para asegurar que Angular actualice la vista
        this.cdr.detectChanges();
        Swal.fire('Error', 'No se pudieron cargar las solicitudes de cambio de residencia', 'error');
      }
    });
  }

  getResidenceChangeStatusLabel(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'approved':
        return 'Aprobada';
      case 'rejected':
        return 'Rechazada';
      case 'expiring_soon':
        return 'Actualizar';
      case 'expired':
        return 'Caducada';
      default:
        return 'Desconocido';
    }
  }

  async approveResidenceChange(request: ResidenceChangeRequest): Promise<void> {
    try {
      // Primero pedir la fecha de validez
      const { value: validityDate } = await Swal.fire({
        title: 'Seleccionar fecha de validez',
        html: `
          <p>¿Estás seguro de que deseas aprobar el cambio de residencia fiscal para este usuario?</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'} (${request.userEmail || 'N/A'})</p>
          <p><strong>Dirección actual:</strong> ${request.currentAddress?.street || 'N/A'}, ${request.currentAddress?.locality || 'N/A'}</p>
          <p><strong>Nueva dirección:</strong> ${request.requestedAddress?.street || 'N/A'}, ${request.requestedAddress?.locality || 'N/A'}</p>
          <p><strong>País actual:</strong> ${request.currentCountry || 'España'}</p>
          <p><strong>País solicitado:</strong> ${request.requestedCountry}</p>
        `,
        input: 'date',
        inputLabel: 'Fecha de validez del certificado',
        inputValue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 año por defecto
        inputAttributes: {
          min: new Date().toISOString().split('T')[0], // No permitir fechas pasadas
          class: 'swal2-date-input'
        },
        inputValidator: (value) => {
          if (!value) {
            return 'Debes seleccionar una fecha de validez';
          }
          return null;
        },
        showCancelButton: true,
        confirmButtonText: 'Aprobar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        didOpen: () => {
          // Aplicar estilos para asegurar visibilidad del input de fecha
          setTimeout(() => {
            const input = document.querySelector('.swal2-input[type="date"]') as HTMLInputElement;
            if (input) {
              // Estilos para asegurar visibilidad
              input.style.display = 'block';
              input.style.visibility = 'visible';
              input.style.opacity = '1';
              input.style.width = '100%';
              input.style.maxWidth = '300px';
              input.style.margin = '10px auto';
              input.style.padding = '10px';
              input.style.fontSize = '16px';
              input.style.textAlign = 'center';
              input.style.border = '2px solid #3085d6';
              input.style.borderRadius = '5px';
              input.style.backgroundColor = '#fff';
              input.style.color = '#000';
              input.style.cursor = 'pointer';
              
              // Asegurar que el contenedor también sea visible
              const inputContainer = input.closest('.swal2-input-container');
              if (inputContainer) {
                (inputContainer as HTMLElement).style.display = 'block';
                (inputContainer as HTMLElement).style.visibility = 'visible';
              }
            }
          }, 100);
        }
      });

      if (validityDate && request.id) {
        // Convertir la fecha a Timestamp
        const validityTimestamp = firebase.firestore.Timestamp.fromDate(new Date(validityDate));
        
        // Actualizar el estado de la solicitud con la fecha de validez
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          status: 'approved',
          approvalDate: firebase.firestore.Timestamp.now(),
          validityDate: validityTimestamp
        });

        // Verificar si el usuario existe antes de intentar actualizarlo
        console.log('🔍 [DEBUG] Admin - Actualizando perfil del usuario:', request.userId);
        const userDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
        
        if (userDoc && userDoc.exists) {
          const userData = userDoc.data() as any;
          console.log('🔍 [DEBUG] Admin - Usuario encontrado, actualizando perfil');
          console.log('🔍 [DEBUG] Admin - Datos actuales del usuario:', userData);
          
          // Usar el porcentaje de retención que ya viene en la solicitud, o calcularlo como fallback
          let countryRetention: number;
          if (request.requestedCountryRetentionPercentage !== undefined && request.requestedCountryRetentionPercentage !== null) {
            countryRetention = request.requestedCountryRetentionPercentage;
            console.log('🔍 [DEBUG] Admin - Usando porcentaje de retención de la solicitud:', countryRetention);
          } else {
            console.log('🔍 [DEBUG] Admin - Calculando porcentaje de retención para:', request.requestedCountry);
            countryRetention = await this.getCountryRetentionPercentage(request.requestedCountry);
            console.log('🔍 [DEBUG] Admin - Porcentaje calculado:', countryRetention);
          }

          console.log('🔍 [DEBUG] Admin - País solicitado:', request.requestedCountry);
          console.log('🔍 [DEBUG] Admin - Porcentaje de retención final:', countryRetention);
          console.log('🔍 [DEBUG] Admin - Dirección solicitada:', request.requestedAddress);
          console.log('🔍 [DEBUG] Admin - Usuario tiene empresa:', !!(userData?.Empresa?.Datos_Empresa || userData?.empresa));

          // Construir el objeto de actualización según el tipo de usuario (empresa o particular)
          const updateData = this.buildResidenceChangeUpdateData(
            userData,
            request.requestedCountry,
            request.requestedAddress,
            countryRetention
          );

          console.log('🔍 [DEBUG] Admin - Datos a actualizar:', updateData);
          console.log('🔍 [DEBUG] Admin - retentionPercentage en updateData:', updateData['particular.retentionPercentage']);
          
          try {
            await this.afs.collection('users').doc(request.userId).update(updateData);
            console.log('🔍 [DEBUG] Admin - Perfil actualizado exitosamente');
            
            // Verificar que se actualizó correctamente
            const updatedUserDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
            const updatedUserData = updatedUserDoc?.data() as any;
            console.log('🔍 [DEBUG] Admin - Datos después de la actualización:', updatedUserData);
            
            // Verificar que retentionPercentage se actualizó en particular (reparto dividendos lee de ahí)
            if (updatedUserData?.particular) {
              console.log('🔍 [DEBUG] Admin - particular.retentionPercentage después de actualizar:', updatedUserData.particular?.retentionPercentage);
            }
          } catch (error) {
            console.error('🔍 [DEBUG] Admin - Error al actualizar perfil:', error);
          }
        } else {
          console.warn(`🔍 [DEBUG] Admin - Usuario ${request.userId} no encontrado en la base de datos`);
        }

        const userExists = userDoc && userDoc.exists;
        Swal.fire(
          '¡Aprobado!',
          `La solicitud de cambio de residencia ha sido aprobada.${userExists ? ' Los datos del usuario han sido actualizados.' : ' Nota: El usuario no fue encontrado en la base de datos.'}`,
          'success'
        );

        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al aprobar la solicitud:', error);
      let errorMessage = 'No se pudo aprobar la solicitud';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'La solicitud o el usuario no fue encontrado';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  async setValidityDate(request: ResidenceChangeRequest): Promise<void> {
    try {
      // Pedir la fecha de validez
      const { value: validityDate } = await Swal.fire({
        title: 'Establecer fecha de validez',
        html: `
          <p>Establecer fecha de validez para la solicitud aprobada:</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'} (${request.userEmail || 'N/A'})</p>
          <p><strong>País:</strong> ${request.requestedCountry}</p>
        `,
        input: 'date',
        inputLabel: 'Fecha de validez del certificado',
        inputValue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 año por defecto
        inputAttributes: {
          min: new Date().toISOString().split('T')[0], // No permitir fechas pasadas
          class: 'swal2-date-input'
        },
        inputValidator: (value) => {
          if (!value) {
            return 'Debes seleccionar una fecha de validez';
          }
          return null;
        },
        showCancelButton: true,
        confirmButtonText: 'Establecer',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        didOpen: () => {
          // Aplicar estilos para asegurar visibilidad del input de fecha
          setTimeout(() => {
            const input = document.querySelector('.swal2-input[type="date"]') as HTMLInputElement;
            if (input) {
              // Estilos para asegurar visibilidad
              input.style.display = 'block';
              input.style.visibility = 'visible';
              input.style.opacity = '1';
              input.style.width = '100%';
              input.style.maxWidth = '300px';
              input.style.margin = '10px auto';
              input.style.padding = '10px';
              input.style.fontSize = '16px';
              input.style.textAlign = 'center';
              input.style.border = '2px solid #3085d6';
              input.style.borderRadius = '5px';
              input.style.backgroundColor = '#fff';
              input.style.color = '#000';
              input.style.cursor = 'pointer';
              
              // Asegurar que el contenedor también sea visible
              const inputContainer = input.closest('.swal2-input-container');
              if (inputContainer) {
                (inputContainer as HTMLElement).style.display = 'block';
                (inputContainer as HTMLElement).style.visibility = 'visible';
              }
            }
          }, 100);
        }
      });

      if (validityDate && request.id) {
        // Convertir la fecha a Timestamp
        const validityTimestamp = firebase.firestore.Timestamp.fromDate(new Date(validityDate));
        
        // Actualizar la solicitud con la fecha de validez
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          validityDate: validityTimestamp
        });

        Swal.fire(
          'Fecha establecida',
          'La fecha de validez ha sido establecida correctamente',
          'success'
        );

        // Actualizar estados automáticamente después de establecer la fecha de validez
        await this.updateResidenceChangeStatuses();

        // Recargar las solicitudes
        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al establecer fecha de validez:', error);
      let errorMessage = 'No se pudo establecer la fecha de validez';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'La solicitud no fue encontrada';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  async rejectResidenceChange(request: ResidenceChangeRequest): Promise<void> {
    try {
      const result = await Swal.fire({
        title: 'Rechazar cambio de residencia',
        input: 'textarea',
        inputLabel: 'Motivo del rechazo',
        inputPlaceholder: 'Escribe el motivo del rechazo...',
        inputAttributes: {
          'aria-label': 'Motivo del rechazo'
        },
        showCancelButton: true,
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        inputValidator: (value) => {
          if (!value) {
            return 'Debes escribir un motivo para el rechazo';
          }
          return null;
        }
      });

      if (result.isConfirmed && request.id) {
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          status: 'rejected',
          rejectionDate: firebase.firestore.Timestamp.now(),
          rejectionReason: result.value
        });

        // Aplicar retención General de Firebase al usuario (documento rechazado = % General)
        const userDoc = await this.afs.collection('users').doc(request.userId).get().toPromise();
        if (userDoc?.exists) {
          const generalRetention = await this.getGeneralRetentionPercentage();
          await this.fiscalDocService.updateUserRetentionPercentage(request.userId, generalRetention).toPromise();
        }

        Swal.fire(
          'Rechazado',
          'La solicitud de cambio de residencia ha sido rechazada. Se ha aplicado la retención general al usuario.',
          'success'
        );

        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al rechazar la solicitud:', error);
      let errorMessage = 'No se pudo rechazar la solicitud';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'not-found') {
        errorMessage = 'La solicitud no fue encontrada';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  }

  // Método para extender la fecha de validez de una solicitud próxima a caducar
  async extendValidityDate(request: ResidenceChangeRequest): Promise<void> {
    try {
      const { value: newValidityDate } = await Swal.fire({
        title: 'Extender fecha de validez',
        html: `
          <p>¿Estás seguro de que deseas extender la fecha de validez de esta solicitud?</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'} (${request.userEmail || 'N/A'})</p>
          <p><strong>País solicitado:</strong> ${request.requestedCountry}</p>
        `,
        input: 'date',
        inputLabel: 'Nueva fecha de validez',
        inputValue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 año por defecto
        inputAttributes: {
          min: new Date().toISOString().split('T')[0], // No permitir fechas pasadas
          class: 'swal2-date-input'
        },
        inputValidator: (value) => {
          if (!value) {
            return 'Debes seleccionar una nueva fecha de validez';
          }
          return null;
        },
        showCancelButton: true,
        confirmButtonText: 'Extender',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        didOpen: () => {
          // Aplicar estilos para asegurar visibilidad del input de fecha
          setTimeout(() => {
            const input = document.querySelector('.swal2-input[type="date"]') as HTMLInputElement;
            if (input) {
              // Estilos para asegurar visibilidad
              input.style.display = 'block';
              input.style.visibility = 'visible';
              input.style.opacity = '1';
              input.style.width = '100%';
              input.style.maxWidth = '300px';
              input.style.margin = '10px auto';
              input.style.padding = '10px';
              input.style.fontSize = '16px';
              input.style.textAlign = 'center';
              input.style.border = '2px solid #3085d6';
              input.style.borderRadius = '5px';
              input.style.backgroundColor = '#fff';
              input.style.color = '#000';
              input.style.cursor = 'pointer';
              
              // Asegurar que el contenedor también sea visible
              const inputContainer = input.closest('.swal2-input-container');
              if (inputContainer) {
                (inputContainer as HTMLElement).style.display = 'block';
                (inputContainer as HTMLElement).style.visibility = 'visible';
              }
            }
          }, 100);
        }
      });

      if (newValidityDate && request.id) {
        const validityTimestamp = firebase.firestore.Timestamp.fromDate(new Date(newValidityDate));
        
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          validityDate: validityTimestamp,
          status: 'approved', // Volver a estado aprobado
          lastStatusUpdate: firebase.firestore.Timestamp.now()
        });

        // Asegurar que el retentionPercentage del usuario esté actualizado
        const userDocRef = this.afs.collection('users').doc(request.userId);
        const userDoc = await userDocRef.get().toPromise();
        if (userDoc && userDoc.exists) {
          const userData = userDoc.data() as any;
          let countryRetention: number;
          if (request.requestedCountryRetentionPercentage !== undefined && request.requestedCountryRetentionPercentage !== null) {
            countryRetention = request.requestedCountryRetentionPercentage;
            console.log('🔍 [DEBUG] extendValidityDate - Usando porcentaje de la solicitud:', countryRetention);
          } else {
            console.log('🔍 [DEBUG] extendValidityDate - Calculando porcentaje para:', request.requestedCountry);
            countryRetention = await this.getCountryRetentionPercentage(request.requestedCountry);
            console.log('🔍 [DEBUG] extendValidityDate - Porcentaje calculado:', countryRetention);
          }
          
          // Construir el objeto de actualización según el tipo de usuario (solo retención, sin dirección)
          const updates = this.buildResidenceChangeUpdateData(
            userData,
            request.requestedCountry,
            null, // No actualizar dirección en extendValidityDate
            countryRetention
          );

          console.log('🔍 [DEBUG] extendValidityDate - Datos a actualizar:', updates);
          await userDocRef.update(updates);
          
          // Verificar que se actualizó correctamente
          const updatedUserDoc = await userDocRef.get().toPromise();
          const updatedUserData = updatedUserDoc?.data() as any;
          if (updatedUserData?.Empresa?.Datos_Empresa) {
            console.log('🔍 [DEBUG] extendValidityDate - particular.retentionPercentage después de actualizar:', updatedUserData.particular?.retentionPercentage);
          }
        }

        Swal.fire(
          '¡Extendido!',
          'La fecha de validez ha sido extendida correctamente.',
          'success'
        );

        // Actualizar estados automáticamente después de extender la fecha de validez
        await this.updateResidenceChangeStatuses();
        
        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al extender la fecha de validez:', error);
      Swal.fire('Error', 'No se pudo extender la fecha de validez.', 'error');
    }
  }

  // Método para renovar una solicitud caducada
  async renewExpiredRequest(request: ResidenceChangeRequest): Promise<void> {
    try {
      const { value: newValidityDate } = await Swal.fire({
        title: 'Renovar solicitud caducada',
        html: `
          <p>¿Estás seguro de que deseas renovar esta solicitud caducada?</p>
          <p><strong>Usuario:</strong> ${request.userName || 'N/A'} (${request.userEmail || 'N/A'})</p>
          <p><strong>País solicitado:</strong> ${request.requestedCountry}</p>
        `,
        input: 'date',
        inputLabel: 'Nueva fecha de validez',
        inputValue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 año por defecto
        inputAttributes: {
          min: new Date().toISOString().split('T')[0], // No permitir fechas pasadas
          class: 'swal2-date-input'
        },
        inputValidator: (value) => {
          if (!value) {
            return 'Debes seleccionar una nueva fecha de validez';
          }
          return null;
        },
        showCancelButton: true,
        confirmButtonText: 'Renovar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#d33',
        didOpen: () => {
          // Aplicar estilos para asegurar visibilidad del input de fecha
          setTimeout(() => {
            const input = document.querySelector('.swal2-input[type="date"]') as HTMLInputElement;
            if (input) {
              // Estilos para asegurar visibilidad
              input.style.display = 'block';
              input.style.visibility = 'visible';
              input.style.opacity = '1';
              input.style.width = '100%';
              input.style.maxWidth = '300px';
              input.style.margin = '10px auto';
              input.style.padding = '10px';
              input.style.fontSize = '16px';
              input.style.textAlign = 'center';
              input.style.border = '2px solid #28a745';
              input.style.borderRadius = '5px';
              input.style.backgroundColor = '#fff';
              input.style.color = '#000';
              input.style.cursor = 'pointer';
              
              // Asegurar que el contenedor también sea visible
              const inputContainer = input.closest('.swal2-input-container');
              if (inputContainer) {
                (inputContainer as HTMLElement).style.display = 'block';
                (inputContainer as HTMLElement).style.visibility = 'visible';
              }
            }
          }, 100);
        }
      });

      if (newValidityDate && request.id) {
        const validityTimestamp = firebase.firestore.Timestamp.fromDate(new Date(newValidityDate));
        
        await this.afs.collection('residence-change-requests').doc(request.id).update({
          validityDate: validityTimestamp,
          status: 'approved', // Volver a estado aprobado
          lastStatusUpdate: firebase.firestore.Timestamp.now()
        });

        // Asegurar que el retentionPercentage del usuario esté actualizado
        const userDocRef = this.afs.collection('users').doc(request.userId);
        const userDoc = await userDocRef.get().toPromise();
        if (userDoc && userDoc.exists) {
          const userData = userDoc.data() as any;
          let countryRetention: number;
          if (request.requestedCountryRetentionPercentage !== undefined && request.requestedCountryRetentionPercentage !== null) {
            countryRetention = request.requestedCountryRetentionPercentage;
            console.log('🔍 [DEBUG] renewExpiredRequest - Usando porcentaje de la solicitud:', countryRetention);
          } else {
            console.log('🔍 [DEBUG] renewExpiredRequest - Calculando porcentaje para:', request.requestedCountry);
            countryRetention = await this.getCountryRetentionPercentage(request.requestedCountry);
            console.log('🔍 [DEBUG] renewExpiredRequest - Porcentaje calculado:', countryRetention);
          }
          
          // Construir el objeto de actualización según el tipo de usuario (solo retención, sin dirección)
          const updates = this.buildResidenceChangeUpdateData(
            userData,
            request.requestedCountry,
            null, // No actualizar dirección en renewExpiredRequest
            countryRetention
          );

          console.log('🔍 [DEBUG] renewExpiredRequest - Datos a actualizar:', updates);
          await userDocRef.update(updates);
          
          // Verificar que se actualizó correctamente
          const updatedUserDoc = await userDocRef.get().toPromise();
          const updatedUserData = updatedUserDoc?.data() as any;
          if (updatedUserData?.Empresa?.Datos_Empresa) {
            console.log('🔍 [DEBUG] renewExpiredRequest - particular.retentionPercentage después de actualizar:', updatedUserData.particular?.retentionPercentage);
          }
        }

        Swal.fire(
          '¡Renovado!',
          'La solicitud ha sido renovada correctamente.',
          'success'
        );

        this.loadResidenceChangeRequests();
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
      }
    } catch (error: any) {
      console.error('Error al renovar la solicitud:', error);
      Swal.fire('Error', 'No se pudo renovar la solicitud.', 'error');
    }
  }

  // Método para actualizar los estados de los cambios de residencia según su fecha de validez
  // showNoChangesMessage: si es true, muestra un mensaje cuando no hay cambios (por defecto true)
  async updateResidenceChangeStatuses(showNoChangesMessage: boolean = true): Promise<void> {
    try {
      const today = new Date();
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      // Obtener todos los cambios de residencia aprobados
      const residenceDocsSnapshot = await this.afs.collection('residence-change-requests', ref => 
        ref.where('status', '==', 'approved')
      ).get().toPromise();

      const batch = this.afs.firestore.batch();
      let updateCount = 0;

      residenceDocsSnapshot?.docs.forEach((doc: any) => {
        const data = doc.data();
        if (data.validityDate) {
          const validityDate = data.validityDate.toDate();
          let newStatus = null;

          // Si ya caducó
          if (validityDate <= today) {
            newStatus = 'expired';
          }
          // Si queda 1 mes o menos para caducar
          else if (validityDate > today && validityDate <= thirtyDaysFromNow) {
            newStatus = 'expiring_soon';
          }

          if (newStatus) {
            batch.update(doc.ref, {
              status: newStatus,
              lastStatusUpdate: firebase.firestore.Timestamp.now()
            });
            updateCount++;
          }
        }
      });

      if (updateCount > 0) {
        await batch.commit();
        // Recargar los datos para reflejar los cambios (la suscripción en tiempo real también actualizará automáticamente)
        if (this.activeTab === 'residenceChanges') {
          // La suscripción en tiempo real actualizará automáticamente, pero forzamos una recarga para asegurar
          this.loadResidenceChangeRequests();
        }
        
        // Actualizar estadísticas si estamos en la pestaña de notificaciones
        if (this.activeTab === 'notifications') {
          this.loadCertificateStatistics();
        }
        
        // Mostrar mensaje de éxito
        Swal.fire(
          'Estados actualizados',
          `Se han actualizado ${updateCount} cambios de residencia con nuevos estados.`,
          'success'
        );
      } else {
        // Mostrar mensaje informativo cuando no hay actualizaciones solo si se solicita explícitamente
        if (showNoChangesMessage) {
          Swal.fire(
            'Sin cambios',
            'No hay cambios de residencia que requieran actualización de estado en este momento.',
            'info'
          );
        }
      }
    } catch (error) {
      console.error('Error al actualizar estados de cambios de residencia:', error);
      Swal.fire(
        'Error',
        'No se pudieron actualizar los estados de los cambios de residencia. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  }

  /** Retención: en Empresa.Datos_Empresa si es empresa; en particular.retentionPercentage si es particular. */
  private addCompanyRetentionFields(target: Record<string, any>, userData: any, retentionPercentage: number): void {
    if (!target || !userData) return;
    const hasEmpresa = userData?.Empresa?.Datos_Empresa || userData?.empresa;
    if (hasEmpresa) {
      target['Empresa.Datos_Empresa.retentionPercentage'] = retentionPercentage;
    } else {
      target['particular.retentionPercentage'] = retentionPercentage;
    }
  }

  /**
   * Construye el objeto de actualización para cambio de residencia según el tipo de usuario
   * - Si tiene empresa: actualiza en Empresa.Datos_Empresa (Pais_Fiscal, Provincia, Codigo_Postal, retentionPercentage)
   * - Si no tiene empresa: actualiza en particular y retentionPercentage dentro de particular
   * @param userData Datos del usuario
   * @param country País de residencia
   * @param address Dirección (opcional, puede ser null)
   * @param retentionPercentage Porcentaje de retención
   */
  private buildResidenceChangeUpdateData(
    userData: any, 
    country: string, 
    address: any | null, 
    retentionPercentage: number
  ): Record<string, any> {
    const updateData: Record<string, any> = {};
    
    // Validar que retentionPercentage sea un número válido
    if (typeof retentionPercentage !== 'number' || isNaN(retentionPercentage)) {
      console.error('❌ [ERROR] buildResidenceChangeUpdateData - retentionPercentage inválido:', retentionPercentage);
      retentionPercentage = 19; // Usar valor por defecto
    }
    
    console.log('🔍 [DEBUG] buildResidenceChangeUpdateData - País:', country, 'Retención:', retentionPercentage);
    
    // Verificar si el usuario tiene empresa
    const hasEmpresa = userData?.Empresa?.Datos_Empresa || userData?.empresa;
    
    // retentionPercentage: en Empresa.Datos_Empresa si empresa; en particular.retentionPercentage si particular
    if (hasEmpresa) {
      updateData['Empresa.Datos_Empresa.retentionPercentage'] = retentionPercentage;
      console.log('🔍 [DEBUG] buildResidenceChangeUpdateData - Actualizando Empresa.Datos_Empresa.retentionPercentage:', retentionPercentage);
    } else {
      updateData['particular.retentionPercentage'] = retentionPercentage;
      console.log('🔍 [DEBUG] buildResidenceChangeUpdateData - Actualizando particular.retentionPercentage:', retentionPercentage);
    }

    if (hasEmpresa) {
      // Usuario con empresa: actualizar país y dirección en Empresa.Datos_Empresa (NO retentionPercentage)
      if (country) {
        updateData['Empresa.Datos_Empresa.pais_de_residencia'] = country;
      }
      if (address) {
        if (address.street) updateData['Empresa.Datos_Empresa.Razon_Social'] = address.street;
        if (address.locality) updateData['Empresa.Datos_Empresa.ciudad'] = address.locality;
        if (address.province) {
          updateData['Empresa.Datos_Empresa.CA_Estado'] = address.province;
          updateData['Empresa.Datos_Empresa.provincia'] = address.province;
        }
        if (address.postalCode) updateData['Empresa.Datos_Empresa.Codigo_Postal'] = address.postalCode;
      }
    } else {
      // Usuario normal: actualizar país y dirección en particular
      if (country) {
        updateData['particular.pais_de_residencia'] = country;
      }
      if (address) {
        if (address.street) updateData['particular.domicilio'] = address.street;
        if (address.locality) updateData['particular.ciudad'] = address.locality;
        if (address.province) updateData['particular.provincia'] = address.province;
        if (address.postalCode) updateData['particular.codigo_postal'] = address.postalCode;
      }
    }
    
    return updateData;  
  }

}
